// @ts-nocheck
import { GetServerSideProps } from 'next';
import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import PairFormSocial from '../components/PairFormSocial';
import AICurationStep from '../components/AICurationStep';
import ArtcastSelection from '../components/ArtcastSelection';
import PairingSuccess from '../components/PairingSuccess';
import Background from '../components/Background';
import ProgressIndicator from '../components/ProgressIndicator';
import PairWithScreen from '../components/PairWithScreen';
import UnifiedWelcomeModal from '../components/UnifiedWelcomeModal';
import WelcomeBackPage from '../components/WelcomeBackPage';
// PAYMENT WALL: Preview pass removed - replaced with payment wall
// import { PreviewPassModal } from '../components/PreviewPassModal';
import { AccountTypeModal } from '../components/AccountTypeModal';
import { AlreadyPairedModal } from '../components/AlreadyPairedModal';
import TVScreen from '../components/TVScreen';
import CampaignModal from '../components/CampaignModal';
import CampaignLoadingOverlay from '../components/CampaignLoadingOverlay';
import SurpriseGiftModal from '../components/SurpriseGiftModal';
import { CampaignErrorToast } from '../components/CampaignErrorToast';
import { PairPageProps, PairingStep, User } from '../types';
// PAYMENT WALL: startPreviewPass removed
import { api } from '../services/api';
import { useAnalytics } from '../hooks/useAnalytics';
import {
    AnalyticsEvents,
    getSessionContext,
    analytics as analyticsProxy,
} from '../utils/analytics';
import { useCampaign } from '../contexts/CampaignContext';
import { useCouponDetection } from '../hooks/useCouponDetection';
import { useUser } from '../contexts/UserContext';
import { ARTCAST_CONFIG } from '../constants/artcastConfig';
import { useABTest, useABTestConversion } from '../hooks/useABTest';
import { PaymentWallModal } from '../components/PaymentWallModal';
import { LastChanceModal } from '../components/LastChanceModal';
import { BillingModal } from '../components/BillingModal';
import logger from '../utils/logger';

export default function PairPage({
    deviceId: initialDeviceId,
    deviceCode: initialDeviceCode,
    shortSecret,
}: PairPageProps) {
    const analytics = useAnalytics();
    const {
        user: sessionUser,
        setUser: setSessionUser,
        isLoading: isUserLoading,
        isPaidPlan,
    } = useUser();

    // Campaign detection and state
    const {
        couponData,
        originalCouponCode,
        showCampaignModal,
        setShowCampaignModal,
        setHasShownModal,
        fromCampaign,
        isLoadingCoupon,
        couponError,
        setCouponError,
    } = useCampaign();

    // PAYMENT WALL: A/B Test for payment wall timing
    const paymentWallTiming = useABTest(
        'payment_wall_timing',
        ['before_pairing', 'after_pairing'],
        'after_pairing' // Default variant
    );
    const trackPaymentWallConversion = useABTestConversion(
        'payment_wall_timing',
        paymentWallTiming
    );

    // Stable callbacks for coupon detection
    const handleCouponSuccess = useCallback((data: any) => {
        console.log(
            '[PAIR-APP] Campaign coupon loaded successfully:',
            data.name
        );
    }, []);

    const handleCouponError = useCallback((error: string) => {
        console.error('[PAIR-APP] Failed to load campaign coupon:', error);
    }, []);

    // Welcome modal state - defined early to pass to useCouponDetection
    // Note: This is initialized early because useCouponDetection needs to know if user has devices
    const [welcomeModalVariant, setWelcomeModalVariant] = useState<
        | 'new_user'
        | 'returning_no_saved'
        | 'returning_with_saved'
        | 'device_already_paired'
    >('new_user');

    // Initialize coupon detection
    // Pass userHasDevices to skip campaign modal for users who already completed signup
    useCouponDetection({
        enabled: true,
        onSuccess: handleCouponSuccess,
        onError: handleCouponError,
        userHasDevices: welcomeModalVariant === 'device_already_paired',
    });

    // Only log when we have actual device parameters
    if (initialDeviceId && initialDeviceCode) {
        console.log('[PAIR-APP] PairPage: Component mounted with props:', {
            deviceId: initialDeviceId,
            deviceCode: initialDeviceCode,
            isShortSecret: initialDeviceCode && initialDeviceCode.length < 10,
        });
    }

    const [currentStep, setCurrentStepState] = useState<PairingStep>('email');
    const [previousStep, setPreviousStep] = useState<PairingStep | null>(null);

    // Define step orders based on flow type
    const getStepOrder = (): PairingStep[] => {
        // Both flows now include the pair_screen step
        return ['email', 'artcast', 'pair_screen', 'success'];
    };

    // Enhanced setCurrentStep that accepts both step names and numbers
    const setCurrentStep = (step: PairingStep | number) => {
        if (typeof step === 'number') {
            const steps = getStepOrder();
            if (step >= 0 && step < steps.length) {
                setPreviousStep(currentStep);
                setCurrentStepState(steps[step]);
            }
        } else {
            setPreviousStep(currentStep);
            setCurrentStepState(step);
        }
    };

    // Helper function to set step by number (0-indexed)
    const setCurrentStepNumber = (stepNumber: number) => {
        setCurrentStep(stepNumber);
    };

    // Helper function to go to first step (skipping email which is step 0)
    const setFirstStep = () => {
        // Always go to artcast (Select) - step 1 for all flows
        setCurrentStep('artcast');
    };

    // Helper function to go to next step
    const setNextStep = () => {
        const steps = getStepOrder();
        const currentIndex = steps.indexOf(currentStep);
        if (currentIndex >= 0 && currentIndex < steps.length - 1) {
            setCurrentStep(currentIndex + 1);
        }
    };

    // Helper function to go to previous step
    const setPreviousStepActive = () => {
        if (previousStep) {
            setCurrentStep(previousStep);
        }
    };
    const [userEmail, setUserEmail] = useState('');
    const [userId, setUserId] = useState<string | null>(null);
    const [user, setUser] = useState<User | null>(null);
    const [selectedChannel, setSelectedChannel] = useState<string | null>(null);
    const [deviceId, setDeviceId] = useState(initialDeviceId);
    const [deviceCode, setDeviceCode] = useState(initialDeviceCode);
    // PAYMENT WALL: Preview pass state removed - replaced with payment wall
    // const [showPreviewPassModal, setShowPreviewPassModal] = useState(false);
    // const [previewPassEndDate, setPreviewPassEndDate] = useState<Date | null>(null);
    const [showDownloadButton, setShowDownloadButton] = useState(false);
    // const [previewPassPending, setPreviewPassPending] = useState(false);

    // PAYMENT WALL: New state for payment wall
    const [showPaymentWallModal, setShowPaymentWallModal] = useState(false);
    const [showLastChanceModal, setShowLastChanceModal] = useState(false);
    const [showBillingModal, setShowBillingModal] = useState(false);
    const [selectedPlanId, setSelectedPlanId] = useState<string | null>(null);
    // Track if upgrade was opened from menu (vs automatic flow)
    // When true, closing modals should NOT redirect the user
    const [upgradeFromMenu, setUpgradeFromMenu] = useState(false);
    const [userAccountType, setUserAccountType] = useState<
        'personal' | 'business'
    >('personal');

    // Sync userAccountType from sessionUser when session is restored
    // This ensures account_type persists across page refreshes
    useEffect(() => {
        if (sessionUser?.account_type) {
            const mappedType =
                sessionUser.account_type === 'private'
                    ? 'personal'
                    : 'business';
            console.log(
                '[PAIR-APP] Syncing userAccountType from session:',
                sessionUser.account_type,
                '→',
                mappedType
            );
            setUserAccountType(mappedType);
        }
    }, [sessionUser?.account_type]);

    // Centralized logic for plan selection
    // - If coupon exists and matches user's account type, use coupon's plan
    // - Otherwise: business gets Pro Annual, personal gets Plus Monthly
    const getPlanToOffer = useCallback(() => {
        // Default plan based on account type
        const defaultPlan =
            userAccountType === 'business'
                ? 'Pro-USD-Yearly'
                : 'PLUS-USD-Monthly';

        // Check if coupon has a plan and matches user's account type
        const couponAccountType =
            couponData?.account_type === 'private'
                ? 'personal'
                : couponData?.account_type;

        if (
            couponData?.payment_provider_user_plans?.[0]?.provider_plan_id &&
            couponAccountType === userAccountType
        ) {
            return couponData.payment_provider_user_plans[0].provider_plan_id;
        }

        return defaultPlan;
    }, [userAccountType, couponData]);

    // Check if user is already on a paid plan (not free)
    // Users who are already upgraded should not see the payment wall
    const isUserOnPaidPlan = useCallback(() => {
        if (!sessionUser?.plan_display_name) return false;
        return sessionUser.plan_display_name.toLowerCase() !== 'free';
    }, [sessionUser?.plan_display_name]);

    const [isNewUser, setIsNewUser] = useState(false);
    const [showAccountTypeModal, setShowAccountTypeModal] = useState(false);
    const [aiDescription, setAiDescription] = useState('');
    const [aiGenres, setAiGenres] = useState<string[]>([]);
    const [aiSearchedChannels, setAiSearchedChannels] = useState<any[] | null>(
        null
    );
    const [showTVScreen, setShowTVScreen] = useState(false);
    const [savedQueue, setSavedQueue] = useState<any>(null);
    const [showWelcomeModal, setShowWelcomeModal] = useState(false);
    // Note: welcomeModalVariant is defined earlier in the file (near useCouponDetection)
    const [isViewingSavedQueue, setIsViewingSavedQueue] = useState(false);
    const [selectedQueueData, setSelectedQueueData] = useState<any>(null);
    const [savedForLater, setSavedForLater] = useState(false);
    const [showDeviceAlreadyPairedModal, setShowDeviceAlreadyPairedModal] =
        useState(false);
    const [alreadyPairedDeviceId, setAlreadyPairedDeviceId] = useState('');
    const [hasCheckedSavedQueue, setHasCheckedSavedQueue] = useState(false);
    const [isCheckingSession, setIsCheckingSession] = useState(false);
    const [isSubscribingToChannel, setIsSubscribingToChannel] = useState(false);
    const [subscriptionComplete, setSubscriptionComplete] = useState(false);
    const [hasShownWelcomeModal, setHasShownWelcomeModal] = useState(false);
    const [isPairing, setIsPairing] = useState(false);

    // AI Personalization Modal state
    const [showAIPersonalizationModal, setShowAIPersonalizationModal] =
        useState(false);
    const [isAIProcessing, setIsAIProcessing] = useState(false);

    // Gift Modal state
    const [showSurpriseGiftModal, setShowSurpriseGiftModal] = useState(false);
    const [giftChannels, setGiftChannels] = useState<any[]>([]);
    const [isLoadingGiftChannel, setIsLoadingGiftChannel] = useState(false);
    const [giftRevealed, setGiftRevealed] = useState(false);

    // Utility function to clear session
    const clearSession = () => {
        console.log('[PAIR-APP] Clearing session data');
        setSessionUser(null);
        setUser(null);
        setUserId('');
        setUserEmail('');
        localStorage.removeItem('jwtToken');
        // Clear auth method data for silent refresh
        localStorage.removeItem('authMethod');
        localStorage.removeItem('authTimestamp');
        sessionStorage.removeItem('triedSilentRefresh');
        // Close any open modals
        setShowAIPersonalizationModal(false);
        setShowWelcomeModal(false);
        setShowAccountTypeModal(false);
        // Reset to email step
        setCurrentStep('email');
    };

    // Wrapper for API calls that handles 401 errors
    const handleApiError = (error: any) => {
        if (
            error?.status === 401 ||
            error?.message?.includes('401') ||
            error?.message?.includes('Unauthorized')
        ) {
            console.log('[PAIR-APP] 401 error detected, clearing session');
            clearSession();
            return true; // Handled
        }
        return false; // Not handled
    };

    // Dynamic step numbering based on the actual flow
    const getAdjustedStepNumber = (step: PairingStep) => {
        const steps = getStepOrder();
        const stepIndex = steps.indexOf(step);

        // Email step is never shown in the progress indicator
        if (step === 'email') {
            return 0;
        }

        // Return the actual position in the current flow
        // Note: We return stepIndex which is already the correct number
        // because email (index 0) is not shown, so index 1 becomes step 1, etc.
        return stepIndex;
    };

    // Auto-skip to connect step if user is already logged in and check for saved queue
    useEffect(() => {
        const checkForSavedQueue = async () => {
            // Only check once, and only for logged-in users
            // Skip if we're in the middle of a new user signup flow
            if (
                !isUserLoading &&
                sessionUser &&
                currentStep === 'email' &&
                !hasCheckedSavedQueue &&
                !isNewUser // Don't interfere with new user flow
            ) {
                // First, ALWAYS check if user already has devices (takes priority)
                // This should run before any coupon/campaign logic
                if (sessionUser?.id) {
                    try {
                        console.log(
                            '[PAIR-APP] Checking user devices first, hasDeviceParams:',
                            !!(deviceId && deviceCode)
                        );
                        const devicesResult = await api.getDevicesByUser();

                        if (devicesResult.success && devicesResult.data) {
                            const userDevices = devicesResult.data;
                            console.log(
                                '[PAIR-APP] User has',
                                userDevices.length,
                                'devices'
                            );

                            if (deviceId && deviceCode) {
                                // CASE 1: User arrived WITH device params
                                // Check if THIS SPECIFIC device is already paired
                                const isThisDevicePaired = userDevices.some(
                                    (device: any) =>
                                        device.id === deviceId ||
                                        device.deviceId === deviceId ||
                                        String(device.id) === String(deviceId)
                                );

                                if (isThisDevicePaired) {
                                    console.log(
                                        '[PAIR-APP] This specific device is already paired, showing success state'
                                    );
                                    setHasCheckedSavedQueue(true);
                                    setWelcomeModalVariant(
                                        'device_already_paired'
                                    );
                                    // No need to setShowWelcomeModal - WelcomeBackPage renders automatically
                                    setHasShownWelcomeModal(true);
                                    return; // Exit early - device is already set up
                                }
                                // If not paired, continue with normal pairing flow
                                console.log(
                                    '[PAIR-APP] Device not yet paired to user, continuing with pairing flow'
                                );
                            } else {
                                // CASE 2: User arrived WITHOUT device params
                                // Check if user has ANY device
                                if (userDevices.length > 0) {
                                    console.log(
                                        '[PAIR-APP] User already has devices, showing success state'
                                    );
                                    setHasCheckedSavedQueue(true);
                                    setWelcomeModalVariant(
                                        'device_already_paired'
                                    );
                                    // No need to setShowWelcomeModal - WelcomeBackPage renders automatically
                                    setHasShownWelcomeModal(true);
                                    return; // Exit early - user already has devices
                                }
                                // No devices, continue with normal flow
                                console.log(
                                    '[PAIR-APP] User has no devices, continuing with normal flow'
                                );
                            }
                        }
                    } catch (error) {
                        console.log(
                            '[PAIR-APP] Error checking device ownership, continuing normal flow:',
                            error
                        );
                        // Continue with normal flow if device check fails
                    }
                }

                // Don't auto-skip if we're loading a coupon or showing campaign modal
                // Campaign modal should be shown first, even for logged-in users
                // The saved queue check will happen after the campaign modal closes
                if (isLoadingCoupon || showCampaignModal || couponData) {
                    console.log(
                        '[PAIR-APP] User logged in but campaign is active, will check saved queue after campaign modal'
                    );
                    // Still set the user data for the session
                    setUser(sessionUser);
                    setUserId(sessionUser.id);
                    setUserEmail(sessionUser.email);
                    // Initialize account type from session user if available
                    // Map 'private' -> 'personal', 'business' -> 'business' for BillingModal
                    if (sessionUser.account_type) {
                        setUserAccountType(
                            sessionUser.account_type === 'private'
                                ? 'personal'
                                : 'business'
                        );
                    }
                    // Don't mark as checked yet - will check after campaign modal
                    return;
                }

                // Mark that we've checked to prevent duplicate checks
                setHasCheckedSavedQueue(true);
                setIsCheckingSession(true);

                console.log(
                    '[PAIR-APP] User already logged in, checking for saved queue'
                );
                console.log('[PAIR-APP] Session user data:', sessionUser);
                setUser(sessionUser);
                setUserId(sessionUser.id);
                setUserEmail(sessionUser.email);
                // Initialize account type from session user if available
                // Map 'private' -> 'personal', 'business' -> 'business' for BillingModal
                if (sessionUser.account_type) {
                    setUserAccountType(
                        sessionUser.account_type === 'private'
                            ? 'personal'
                            : 'business'
                    );
                }

                // Check if user just signed in (within last 30 seconds)
                const authTimestamp = localStorage.getItem('authTimestamp');
                const timeSinceAuth = authTimestamp
                    ? Date.now() - parseInt(authTimestamp)
                    : Infinity;
                const justSignedIn = timeSinceAuth < 30000;

                // Validate session by fetching user details
                try {
                    console.log(
                        '[PAIR-APP] Validating session for logged-in user...'
                    );

                    if (justSignedIn) {
                        console.log(
                            '[PAIR-APP] User just signed in ' +
                                Math.round(timeSinceAuth / 1000) +
                                ' seconds ago, proceeding without validation'
                        );
                    }

                    // First, validate the session by getting fresh user details (skip if just signed in)
                    let sessionIsValid = justSignedIn ? true : false;
                    try {
                        // Only validate if user didn't just sign in
                        if (!justSignedIn) {
                            const userDetailsResult = await api.getUserDetails(
                                sessionUser.id
                            );
                            if (
                                userDetailsResult.success &&
                                userDetailsResult.data
                            ) {
                                console.log(
                                    '[PAIR-APP] Session is valid, user details fetched:',
                                    userDetailsResult.data
                                );

                                // The API returns nested data - extract the actual user data
                                // It could be under data.data, data.User, or directly in data
                                const responseData = userDetailsResult.data;
                                const actualUserData =
                                    responseData.data ||
                                    responseData.User ||
                                    responseData;

                                console.log(
                                    '[PAIR-APP] Extracted user data:',
                                    actualUserData
                                );

                                // Merge with session user, ensuring we preserve the session token and ID
                                const freshUserData = {
                                    ...sessionUser,
                                    ...actualUserData,
                                    id: sessionUser.id, // Preserve session ID
                                    token: sessionUser.token, // Preserve session token
                                };

                                console.log(
                                    '[PAIR-APP] Final merged user data:',
                                    freshUserData
                                );
                                setUser(freshUserData);
                                setSessionUser(freshUserData);
                                sessionIsValid = true;
                            }
                        } else {
                            // User just signed in, use the session user data as-is
                            setUser(sessionUser);
                            setSessionUser(sessionUser);
                            sessionIsValid = true;
                        }

                        // Associate coupon with user if they came from a campaign (only if session is valid)
                        if (sessionIsValid) {
                            if (
                                couponData &&
                                originalCouponCode &&
                                couponData.provider
                            ) {
                                console.log(
                                    '[PAIR-APP] Associating coupon with already logged-in user:',
                                    {
                                        provider: couponData.provider,
                                        couponCode: originalCouponCode,
                                    }
                                );

                                try {
                                    const associateResult =
                                        await api.associateCouponForUser(
                                            couponData.provider as
                                                | 'apple'
                                                | 'charge_bee',
                                            originalCouponCode
                                        );

                                    if (associateResult.success) {
                                        console.log(
                                            '[PAIR-APP] Successfully associated coupon with logged-in user'
                                        );
                                    } else {
                                        console.error(
                                            '[PAIR-APP] Failed to associate coupon:',
                                            associateResult.error
                                        );
                                    }
                                } catch (error) {
                                    console.error(
                                        '[PAIR-APP] Error associating coupon:',
                                        error
                                    );
                                    // Continue with the flow even if coupon association fails
                                }
                            }
                        }
                    } catch (error: any) {
                        console.log(
                            '[PAIR-APP] Error fetching user details:',
                            error
                        );
                        sessionIsValid = false;
                    }

                    // If session is not valid, clear everything and exit
                    if (!sessionIsValid) {
                        console.log(
                            '[PAIR-APP] Session validation failed, clearing session'
                        );
                        // Clear invalid session and close any open modals
                        clearSession();
                        setShowAIPersonalizationModal(false);
                        setIsCheckingSession(false);
                        return; // Exit early, don't continue with invalid session
                    }

                    // Only check saved queue if session is valid
                    const savedQueueResult = await api.getQueueForLater();
                    console.log(
                        '[PAIR-APP] Saved queue API response:',
                        savedQueueResult
                    );

                    if (savedQueueResult.success && savedQueueResult.data) {
                        console.log(
                            '[PAIR-APP] Found saved queue for logged-in user:',
                            savedQueueResult.data
                        );
                        setSavedQueue(savedQueueResult.data);

                        // Check if user has a new coupon that needs to be shown
                        if (
                            couponData &&
                            couponData.is_exists_for_user === false
                        ) {
                            console.log(
                                '[PAIR-APP] User has a new coupon, showing surprise gift modal instead of welcome back'
                            );
                            // Check if we have a gift channel from the campaign
                            if (hasGiftChannels(couponData)) {
                                setGiftRevealed(false); // Reset the flag when showing the modal
                                setShowSurpriseGiftModal(true);
                            } else {
                                // Show AI personalization modal for new coupon without gift
                                setShowAIPersonalizationModal(true);
                            }
                        } else if (!hasShownWelcomeModal) {
                            // Show welcome back modal with saved queue option (only if not already shown)
                            setWelcomeModalVariant('returning_with_saved');
                            setShowWelcomeModal(true);
                            setHasShownWelcomeModal(true);
                        }
                        // Stay on email step to show the modal
                    } else {
                        console.log(
                            '[PAIR-APP] No saved queue found for logged-in user'
                        );

                        // Check if user has a new coupon that needs to be shown
                        if (
                            couponData &&
                            couponData.is_exists_for_user === false
                        ) {
                            console.log(
                                '[PAIR-APP] User has a new coupon, showing surprise gift modal instead of welcome back'
                            );
                            // Check if we have a gift channel from the campaign
                            if (hasGiftChannels(couponData)) {
                                setGiftRevealed(false); // Reset the flag when showing the modal
                                setShowSurpriseGiftModal(true);
                            } else {
                                // Show AI personalization modal for new coupon without gift
                                setShowAIPersonalizationModal(true);
                            }
                        } else if (
                            sessionUser &&
                            sessionUser.id &&
                            !hasShownWelcomeModal
                        ) {
                            // Show welcome back modal without saved queue option (only if not already shown)
                            setWelcomeModalVariant('returning_no_saved');
                            setShowWelcomeModal(true);
                            setHasShownWelcomeModal(true);
                        }
                    }
                } catch (error: any) {
                    console.error(
                        '[PAIR-APP] Error checking saved queue for logged-in user:',
                        error
                    );
                    // For any error checking saved queue, clear session and don't show modals
                    // The session is likely invalid if we can't check the queue
                    console.log(
                        '[PAIR-APP] Failed to check saved queue, clearing session'
                    );
                    // Clear invalid session and close any open modals
                    clearSession();
                    setShowAIPersonalizationModal(false);
                } finally {
                    setIsCheckingSession(false);
                }
            }
        };

        checkForSavedQueue();
    }, [
        isUserLoading,
        sessionUser,
        currentStep,
        hasCheckedSavedQueue,
        deviceId,
        deviceCode,
        isLoadingCoupon,
        showCampaignModal,
        couponData,
        originalCouponCode,
        isNewUser,
        hasShownWelcomeModal,
    ]);

    // Clear isNewUser flag when onboarding is complete
    useEffect(() => {
        if (currentStep === 'success' && isNewUser) {
            console.log(
                '[PAIR-APP] New user reached success, clearing isNewUser flag'
            );
            setIsNewUser(false);
        }
    }, [currentStep, isNewUser]);

    useEffect(() => {
        // Only track if we actually transitioned to a new step
        if (currentStep !== previousStep) {
            if (deviceId && deviceCode) {
                console.log(
                    '[PAIR-APP] PairPage: Step changed from',
                    previousStep,
                    'to:',
                    currentStep
                );
            }

            // Only track pairing-related steps, not artcast selection or saving for later
            // These are actual pairing progress steps that lead to device connection
            const pairingSteps = ['qr', 'pair', 'success'];

            // Only fire event when we transition TO a pairing step (not FROM)
            if (
                pairingSteps.includes(currentStep) &&
                currentStep !== previousStep
            ) {
                analytics.trackPairingEvent('step_completed', {
                    step: currentStep,
                    previous_step: previousStep,
                    device_id: deviceId,
                    has_device_code: !!deviceCode,
                });
            }

            // Update previous step for next comparison
            setPreviousStep(currentStep);
        }
    }, [currentStep, previousStep]); // Track both current and previous to detect transitions

    // Show TV screen after 2 seconds on email step
    useEffect(() => {
        if (currentStep === 'email') {
            const timer = setTimeout(() => {
                setShowTVScreen(true);
            }, 2000);
            return () => clearTimeout(timer);
        } else {
            setShowTVScreen(false);
        }
    }, [currentStep]);

    const handleEmailSubmit = async (
        email: string,
        createdUserId: string,
        userData?: User,
        isNew?: boolean
    ) => {
        console.log('[PAIR-APP] PairPage: handleEmailSubmit called with:', {
            email,
            createdUserId,
            userData,
            isNew,
        });
        setUserEmail(email);
        setUserId(createdUserId);
        if (userData) {
            setUser(userData);
            // Also save to user context for session persistence
            setSessionUser(userData);
        }

        // Associate coupon with user if they came from a campaign
        if (couponData && originalCouponCode && couponData.provider) {
            console.log('[PAIR-APP] Associating coupon with user account:', {
                provider: couponData.provider,
                couponCode: originalCouponCode,
            });

            try {
                const associateResult = await api.associateCouponForUser(
                    couponData.provider as 'apple' | 'charge_bee',
                    originalCouponCode
                );

                if (associateResult.success) {
                    console.log(
                        '[PAIR-APP] Successfully associated coupon with user'
                    );
                } else {
                    console.error(
                        '[PAIR-APP] Failed to associate coupon:',
                        associateResult.error
                    );
                }
            } catch (error) {
                console.error('[PAIR-APP] Error associating coupon:', error);
                // Continue with the flow even if coupon association fails
            }
        }

        if (isNew) {
            setIsNewUser(true);
            // Show account type modal before proceeding to artcast selection
            setShowAccountTypeModal(true);
        } else {
            // Returning user - first check if they already have devices
            try {
                console.log(
                    '[PAIR-APP] Checking user devices after sign-in, hasDeviceParams:',
                    !!(deviceId && deviceCode)
                );
                const devicesResult = await api.getDevicesByUser();

                if (devicesResult.success && devicesResult.data) {
                    const userDevices = devicesResult.data;
                    console.log(
                        '[PAIR-APP] User has',
                        userDevices.length,
                        'devices after sign-in'
                    );

                    if (deviceId && deviceCode) {
                        // User arrived WITH device params - check if THIS device is paired
                        const isThisDevicePaired = userDevices.some(
                            (device: any) =>
                                device.id === deviceId ||
                                device.deviceId === deviceId ||
                                String(device.id) === String(deviceId)
                        );

                        if (isThisDevicePaired) {
                            console.log(
                                '[PAIR-APP] This specific device already paired after sign-in'
                            );
                            setWelcomeModalVariant('device_already_paired');
                            // No need to setShowWelcomeModal - WelcomeBackPage renders automatically
                            setHasShownWelcomeModal(true);
                            return; // Exit early
                        }
                    } else {
                        // User arrived WITHOUT device params - check if they have ANY device
                        if (userDevices.length > 0) {
                            console.log(
                                '[PAIR-APP] User already has devices after sign-in'
                            );
                            setWelcomeModalVariant('device_already_paired');
                            // No need to setShowWelcomeModal - WelcomeBackPage renders automatically
                            setHasShownWelcomeModal(true);
                            return; // Exit early
                        }
                    }
                }
            } catch (error) {
                console.log(
                    '[PAIR-APP] Error checking devices after sign-in:',
                    error
                );
                // Continue with normal flow
            }

            // Check for saved queue
            try {
                console.log('[PAIR-APP] Checking for saved queue...');
                const savedQueueResult = await api.getQueueForLater();

                if (savedQueueResult.success && savedQueueResult.data) {
                    console.log(
                        '[PAIR-APP] Found saved queue:',
                        savedQueueResult.data
                    );
                    setSavedQueue(savedQueueResult.data);
                    setWelcomeModalVariant('returning_with_saved');
                    setShowWelcomeModal(true);
                } else {
                    console.log('[PAIR-APP] No saved queue found');

                    // Check if user has a new coupon that needs to be shown
                    if (couponData && couponData.is_exists_for_user === false) {
                        console.log(
                            '[PAIR-APP] User has a new coupon, showing surprise gift modal instead of welcome back'
                        );
                        // Check if we have a gift channel from the campaign
                        if (hasGiftChannels(couponData)) {
                            setGiftRevealed(false); // Reset the flag when showing the modal
                            setShowSurpriseGiftModal(true);
                        } else {
                            // Show AI personalization modal for new coupon without gift
                            setShowAIPersonalizationModal(true);
                        }
                    } else {
                        // Show welcome back modal for returning users without saved queue
                        setWelcomeModalVariant('returning_no_saved');
                        setShowWelcomeModal(true);
                    }
                }
            } catch (error: any) {
                console.error('[PAIR-APP] Error checking saved queue:', error);
                // Don't show modal if there's an error - session might be invalid
                if (error?.status === 401 || error?.message?.includes('401')) {
                    console.log(
                        '[PAIR-APP] 401 error checking saved queue, clearing session'
                    );
                    clearSession();
                } else {
                    // Only show modal for non-auth errors if we have a valid session
                    if (user && user.id) {
                        // Check if user has a new coupon that needs to be shown
                        if (
                            couponData &&
                            couponData.is_exists_for_user === false
                        ) {
                            console.log(
                                '[PAIR-APP] User has a new coupon (error case), showing surprise gift modal instead of welcome back'
                            );
                            // Check if we have a gift channel from the campaign
                            if (hasGiftChannels(couponData)) {
                                setGiftRevealed(false); // Reset the flag when showing the modal
                                setShowSurpriseGiftModal(true);
                            } else {
                                // Show AI personalization modal for new coupon without gift
                                setShowAIPersonalizationModal(true);
                            }
                        } else {
                            setWelcomeModalVariant(
                                isNewUser ? 'new_user' : 'returning_no_saved'
                            );
                            setShowWelcomeModal(true);
                            setHasShownWelcomeModal(true);
                        }
                    }
                }
            }
        }
    };

    const handleAccountTypeSelection = async (
        accountType: 'personal' | 'business'
    ) => {
        console.log('[PAIR-APP] Account type selected:', accountType);

        // PAYMENT WALL: Store account type for plan selection
        setUserAccountType(accountType);

        // Transition immediately - business update runs in background (non-blocking)
        setShowAccountTypeModal(false);

        // Check if user has a new coupon that needs to be shown
        if (couponData && couponData.is_exists_for_user === false) {
            console.log(
                '[PAIR-APP] New user has a new coupon, showing surprise gift modal instead of welcome back'
            );
            // Check if we have a gift channel from the campaign
            if (hasGiftChannels(couponData)) {
                setGiftRevealed(false); // Reset the flag when showing the modal
                setShowSurpriseGiftModal(true);
            } else {
                // For non-gift flows, load default featured channels directly
                console.log(
                    '[PAIR-APP] Non-gift flow - loading featured channels directly'
                );
                loadFeaturedChannelsAndProceed();
            }
        } else {
            // Show welcome modal immediately for smooth transition
            setWelcomeModalVariant(
                isNewUser ? 'new_user' : 'returning_no_saved'
            );
            setShowWelcomeModal(true);
            setHasShownWelcomeModal(true);
        }

        // Handle business account updates in background (non-blocking)
        if (accountType === 'business' && userId) {
            // Fire and forget - don't await, uses skipSessionRefresh to prevent race conditions
            (async () => {
                try {
                    const userDetailsResult = await api.getUserDetails(userId);
                    if (userDetailsResult.success && userDetailsResult.data) {
                        const responseData = userDetailsResult.data;
                        const currentUserData =
                            responseData.data ||
                            responseData.User ||
                            responseData;
                        if (currentUserData.email && currentUserData.id) {
                            await api.updateUserProfile(
                                userId,
                                currentUserData,
                                {
                                    titles: ['5'],
                                    organization_type: 'BS',
                                },
                                { skipSessionRefresh: true } // Prevent session race condition with concurrent API calls
                            );
                        }
                    }
                } catch (error) {
                    console.error(
                        '[PAIR-APP] Background business update error:',
                        error
                    );
                }
            })();
        }
    };

    const handleAICurationSubmit = async (
        fullDescription: string,
        selectedGenres: any[]
    ) => {
        console.log('[PAIR-APP] AI Curation submitted:', fullDescription);
        console.log('[PAIR-APP] Selected genres:', selectedGenres);

        // Extract just the text description (before "Genres:")
        const descriptionParts = fullDescription.split('\nGenres:');
        const textDescription = descriptionParts[0].trim();

        // Extract genre codes
        const genreCodes = selectedGenres.map((g) => g.code);

        setAiDescription(textDescription);
        setAiGenres(genreCodes);
        setIsAIProcessing(true);

        // Start searching for channels immediately
        const searchChannels = async () => {
            // Check if we have any search criteria
            const hasSearchCriteria =
                (searchText && searchText.trim()) ||
                (themeIds && themeIds.length > 0) ||
                (tagNames && tagNames.length > 0);

            if (!hasSearchCriteria) {
                console.log(
                    '[PAIR-APP] No search criteria, fetching featured channels directly'
                );
                // No search criteria, go straight to featured channels
                try {
                    const featuredResult = await api.getFeaturedChannels();
                    if (featuredResult.success && featuredResult.data) {
                        setAiSearchedChannels(
                            featuredResult.data.slice(
                                0,
                                ARTCAST_CONFIG.MAX_DISPLAY_COUNT
                            )
                        );
                    }
                } catch (error) {
                    console.error(
                        '[PAIR-APP] Error fetching featured channels:',
                        error
                    );
                }
                return;
            }

            console.log('[PAIR-APP] Searching channels with AI curation:', {
                searchText: searchText,
                themes: themeIds,
                tags: tagNames,
            });

            try {
                // Search for channels using the AI curation data with themes and tags
                const searchResult = await api.searchChannels(
                    searchText || '',
                    themeIds || [],
                    tagNames || [],
                    ARTCAST_CONFIG.MAX_DISPLAY_COUNT // Request channels
                );

                if (
                    searchResult.success &&
                    searchResult.data &&
                    searchResult.data.length > 0
                ) {
                    const searchChannels = searchResult.data;
                    console.log(
                        '[PAIR-APP] Search returned',
                        searchChannels.length,
                        'channels'
                    );

                    // If we got less than required channels, fetch featured channels to fill the gap
                    if (
                        searchChannels.length < ARTCAST_CONFIG.MAX_DISPLAY_COUNT
                    ) {
                        console.log(
                            `[PAIR-APP] Need to fetch featured channels to fill up to ${ARTCAST_CONFIG.MAX_DISPLAY_COUNT}`
                        );

                        const featuredResult = await api.getFeaturedChannels();
                        if (featuredResult.success && featuredResult.data) {
                            const featuredChannels = featuredResult.data;

                            // Filter out channels that are already in search results
                            const searchChannelIds = new Set(
                                searchChannels.map((c) => c.id)
                            );
                            const additionalChannels = featuredChannels
                                .filter((c) => !searchChannelIds.has(c.id))
                                .slice(
                                    0,
                                    ARTCAST_CONFIG.MAX_DISPLAY_COUNT -
                                        searchChannels.length
                                );

                            // Combine search results with featured channels
                            const combinedChannels = [
                                ...searchChannels,
                                ...additionalChannels,
                            ];
                            console.log(
                                '[PAIR-APP] Combined channels:',
                                combinedChannels.length
                            );
                            setAiSearchedChannels(combinedChannels);
                        } else {
                            // Just use what we got from search
                            setAiSearchedChannels(searchChannels);
                        }
                    } else {
                        // We have enough channels from search
                        setAiSearchedChannels(
                            searchChannels.slice(
                                0,
                                ARTCAST_CONFIG.MAX_DISPLAY_COUNT
                            )
                        );
                    }
                } else {
                    console.log(
                        '[PAIR-APP] No search results or search failed, falling back to featured channels'
                    );
                    // No results or search failed, fall back to featured channels
                    const featuredResult = await api.getFeaturedChannels();
                    if (featuredResult.success && featuredResult.data) {
                        setAiSearchedChannels(
                            featuredResult.data.slice(
                                0,
                                ARTCAST_CONFIG.MAX_DISPLAY_COUNT
                            )
                        );
                    }
                }
            } catch (error) {
                console.error('[PAIR-APP] Error searching channels:', error);
                // Fall back to featured channels on error
                try {
                    const featuredResult = await api.getFeaturedChannels();
                    if (featuredResult.success && featuredResult.data) {
                        setAiSearchedChannels(
                            featuredResult.data.slice(
                                0,
                                ARTCAST_CONFIG.MAX_DISPLAY_COUNT
                            )
                        );
                    }
                } catch (featuredError) {
                    console.error(
                        '[PAIR-APP] Error fetching featured channels:',
                        featuredError
                    );
                }
            }
        };

        // Start search immediately
        searchChannels();

        // Wait for 5.5 seconds then proceed to artcast
        setTimeout(() => {
            setIsAIProcessing(false);
            setCurrentStep('artcast');
        }, 5500);
    };

    // New handler for AI Personalization Modal
    const handleAIPersonalizationModalSubmit = async (
        searchText: string,
        selectedThemes: any[],
        selectedTags: any[]
    ) => {
        console.log(
            '[PAIR-APP] AI Personalization Modal submitted:',
            searchText
        );
        console.log('[PAIR-APP] Selected themes:', selectedThemes);
        console.log('[PAIR-APP] Selected tags:', selectedTags);

        // Clear saved queue state since user is starting fresh with AI
        setIsViewingSavedQueue(false);
        setSavedQueue(null);

        // Start processing
        setIsAIProcessing(true);

        // Extract theme and tag information
        const themeIds = selectedThemes.map((t) => t.id);
        const tagNames = selectedTags.map((t) => t.name);

        setAiDescription(searchText);
        setAiGenres(themeIds); // Temporarily reuse this for themes

        // Start searching for channels immediately
        const searchChannels = async () => {
            // Check if we have any search criteria
            const hasSearchCriteria =
                (searchText && searchText.trim()) ||
                (themeIds && themeIds.length > 0) ||
                (tagNames && tagNames.length > 0);

            if (!hasSearchCriteria) {
                console.log(
                    '[PAIR-APP] No search criteria, fetching featured channels directly'
                );
                // No search criteria, go straight to featured channels
                try {
                    const featuredResult = await api.getFeaturedChannels();
                    if (featuredResult.success && featuredResult.data) {
                        setAiSearchedChannels(
                            featuredResult.data.slice(
                                0,
                                ARTCAST_CONFIG.MAX_DISPLAY_COUNT
                            )
                        );
                    }
                } catch (error) {
                    console.error(
                        '[PAIR-APP] Error fetching featured channels:',
                        error
                    );
                }
                return;
            }

            console.log('[PAIR-APP] Searching channels with AI curation:', {
                searchText: searchText,
                themes: themeIds,
                tags: tagNames,
            });

            try {
                // Search for channels using the AI curation data with themes and tags
                const searchResult = await api.searchChannels(
                    searchText || '',
                    themeIds || [],
                    tagNames || [],
                    ARTCAST_CONFIG.MAX_DISPLAY_COUNT // Request channels
                );

                if (
                    searchResult.success &&
                    searchResult.data &&
                    searchResult.data.length > 0
                ) {
                    const searchChannels = searchResult.data;
                    console.log(
                        '[PAIR-APP] Search returned',
                        searchChannels.length,
                        'channels'
                    );

                    // If we got less than required channels, fetch featured channels to fill the gap
                    if (
                        searchChannels.length < ARTCAST_CONFIG.MAX_DISPLAY_COUNT
                    ) {
                        console.log(
                            `[PAIR-APP] Need to fetch featured channels to fill up to ${ARTCAST_CONFIG.MAX_DISPLAY_COUNT}`
                        );

                        const featuredResult = await api.getFeaturedChannels();
                        if (featuredResult.success && featuredResult.data) {
                            const featuredChannels = featuredResult.data;

                            // Filter out channels that are already in search results
                            const searchChannelIds = new Set(
                                searchChannels.map((c) => c.id)
                            );
                            const additionalChannels = featuredChannels
                                .filter((c) => !searchChannelIds.has(c.id))
                                .slice(
                                    0,
                                    ARTCAST_CONFIG.MAX_DISPLAY_COUNT -
                                        searchChannels.length
                                );

                            // Combine search results with featured channels
                            const combinedChannels = [
                                ...searchChannels,
                                ...additionalChannels,
                            ];
                            console.log(
                                '[PAIR-APP] Combined channels:',
                                combinedChannels.length
                            );
                            setAiSearchedChannels(combinedChannels);
                        } else {
                            // Just use what we got from search
                            setAiSearchedChannels(searchChannels);
                        }
                    } else {
                        // We have enough channels from search
                        setAiSearchedChannels(
                            searchChannels.slice(
                                0,
                                ARTCAST_CONFIG.MAX_DISPLAY_COUNT
                            )
                        );
                    }
                } else {
                    console.log(
                        '[PAIR-APP] No search results or search failed, falling back to featured channels'
                    );
                    // No results or search failed, fall back to featured channels
                    const featuredResult = await api.getFeaturedChannels();
                    if (featuredResult.success && featuredResult.data) {
                        setAiSearchedChannels(
                            featuredResult.data.slice(
                                0,
                                ARTCAST_CONFIG.MAX_DISPLAY_COUNT
                            )
                        );
                    }
                }
            } catch (error) {
                console.error('[PAIR-APP] Error searching channels:', error);
                // Fall back to featured channels on error
                try {
                    const featuredResult = await api.getFeaturedChannels();
                    if (featuredResult.success && featuredResult.data) {
                        setAiSearchedChannels(
                            featuredResult.data.slice(
                                0,
                                ARTCAST_CONFIG.MAX_DISPLAY_COUNT
                            )
                        );
                    }
                } catch (featuredError) {
                    console.error(
                        '[PAIR-APP] Error fetching featured channels:',
                        featuredError
                    );
                }
            }
        };

        // Start search immediately
        searchChannels();

        // Wait for 5.5 seconds then proceed to choose step and close modal
        setTimeout(() => {
            setIsAIProcessing(false);
            setShowAIPersonalizationModal(false);
            setCurrentStep('artcast');
        }, 5500);
    };

    // Handle Welcome Modal actions
    const handleWelcomeUseSaved = () => {
        console.log(
            '[PAIR-APP] User chose to use saved queue from welcome modal'
        );
        setShowWelcomeModal(false);
        // Don't clear isNewUser here - keep it true to prevent "Welcome back" modal
        // Continue with saved queue
        handleUseSavedQueue();
    };

    // Helper function to check if coupon has gift channels
    const hasGiftChannels = (couponData: any) => {
        return !!(
            couponData?.distribution_conditions?.auto_subscribe &&
            (typeof couponData.distribution_conditions.auto_subscribe ===
                'string' ||
                (Array.isArray(
                    couponData.distribution_conditions.auto_subscribe
                ) &&
                    couponData.distribution_conditions.auto_subscribe.length >
                        0))
        );
    };

    // Helper function to get gift channel IDs
    const getGiftChannelIds = (couponData: any): string | string[] => {
        if (!couponData?.distribution_conditions?.auto_subscribe) {
            return [];
        }
        return couponData.distribution_conditions.auto_subscribe;
    };

    const handleWelcomeStartFresh = () => {
        console.log('[PAIR-APP] User chose to start fresh from welcome modal');
        setShowWelcomeModal(false);
        // Don't clear isNewUser here - keep it true to prevent "Welcome back" modal

        // Show the AI personalization modal with input fields
        console.log('[PAIR-APP] Opening AI curation modal for fresh start');
        setShowAIPersonalizationModal(true);

        // Don't auto-trigger - let user customize in the modal first
        // handleAIPersonalizationModalSubmit('', [], []);
    };

    // Handle reveal gift from surprise modal
    const handleRevealGift = async () => {
        console.log('[PAIR-APP] Revealing gift channel');
        setIsLoadingGiftChannel(true);

        try {
            const channelIds = getGiftChannelIds(couponData);
            console.log('[PAIR-APP] Fetching gift channel(s):', channelIds);

            const result = await api.fetchGiftChannel(channelIds);

            if (result.success && result.data && result.data.length > 0) {
                console.log(
                    '[PAIR-APP] Gift channels fetched successfully:',
                    result.data
                );

                // Store gift channels with isGift flag
                const giftChannelsWithFlag = result.data.map((channel) => ({
                    ...channel,
                    isGift: true,
                }));
                setGiftChannels(giftChannelsWithFlag);

                // Mark that gift was successfully revealed
                setGiftRevealed(true);

                // Close the surprise modal
                setShowSurpriseGiftModal(false);

                // Move to artcast selection with gift channels
                setCurrentStep('artcast');

                // Track gift reveal
                analyticsProxy.capture('gift_channel_revealed', {
                    user_id: userId,
                    campaign_name: couponData?.name,
                    gift_channel_ids: channelIds,
                    channel_count: result.data.length,
                    ...getSessionContext(),
                });
            } else {
                console.error('[PAIR-APP] Failed to fetch gift channels');
                // Still close modal and proceed with normal flow
                setShowSurpriseGiftModal(false);
                setShowAIPersonalizationModal(true);
            }
        } catch (error) {
            console.error('[PAIR-APP] Error fetching gift channels:', error);
            // Fallback to AI personalization
            setShowSurpriseGiftModal(false);
            setShowAIPersonalizationModal(true);
        } finally {
            setIsLoadingGiftChannel(false);
        }
    };

    const handleUseSavedQueue = async () => {
        console.log('[PAIR-APP] User chose to use saved queue');

        // Track that user chose saved queue over AI curation (non-blocking)
        analyticsProxy.capture(AnalyticsEvents.SAVED_QUEUE_SELECTED, {
            user_id: userId,
            choice: 'use_saved_queue',
            saved_queue_id: savedQueue?.id || null,
            saved_channel_id: savedQueue?.queue?.[0]?.channelID || null,
            saved_channel_name:
                savedQueue?.queue?.[0]?.channelData?.name || null,
            artworks_count: savedQueue?.queue?.[0]?.artworks?.length || 0,
            ...getSessionContext(),
        });

        // Set flags BEFORE navigating to prevent any AI processing
        setIsViewingSavedQueue(true);
        setShowAIPersonalizationModal(false); // Explicitly prevent AI modal
        setIsAIProcessing(false); // Ensure no processing state

        // Navigate directly to artcast with saved queue
        setCurrentStep('artcast');
    };

    // Load featured channels for non-gift flows instead of showing AI modal
    const loadFeaturedChannelsAndProceed = async () => {
        console.log('[PAIR-APP] Loading featured channels for non-gift flow');
        setIsAIProcessing(true);

        try {
            const featuredResult = await api.getFeaturedChannels();
            console.log('[PAIR-APP] Featured channels result:', featuredResult);

            if (
                featuredResult.success &&
                featuredResult.data &&
                featuredResult.data.length > 0
            ) {
                // Use the featured channels as the default selection
                const defaultChannels = featuredResult.data.slice(
                    0,
                    ARTCAST_CONFIG.MAX_DISPLAY_COUNT
                ); // Take up to channels
                console.log(
                    '[PAIR-APP] Setting default channels:',
                    defaultChannels
                );

                setAiSearchedChannels(defaultChannels);
                setCurrentStep('artcast');

                // Track that we're using default channels
                analyticsProxy.capture(
                    AnalyticsEvents.DEFAULT_CHANNELS_LOADED,
                    {
                        user_id: userId,
                        channel_count: defaultChannels.length,
                        channel_ids: defaultChannels.map((c) => c.id),
                        ...getSessionContext(),
                    }
                );
            } else {
                console.error('[PAIR-APP] No featured channels available');
                // Fallback: Show AI modal if no featured channels
                setShowAIPersonalizationModal(true);
            }
        } catch (error) {
            console.error('[PAIR-APP] Error loading featured channels:', error);
            // Fallback: Show AI modal on error
            setShowAIPersonalizationModal(true);
        } finally {
            setIsAIProcessing(false);
        }
    };

    const handleStartFreshFromArtcast = () => {
        console.log(
            '[PAIR-APP] User clicked AI Curation button from artcast screen'
        );

        // User explicitly wants AI curation, so show the modal
        setShowAIPersonalizationModal(true);

        // Don't clear saved queue state here - it doesn't affect the modal
        // and clearing it causes unnecessary re-renders that trigger fetching
    };

    // Function to clear device parameters from URL
    const clearDeviceParamsFromURL = () => {
        console.log('[PAIR-APP] Clearing device params from URL');
        console.log('[PAIR-APP] Current URL:', window.location.href);

        // Parse current URL
        const url = new URL(window.location.href);

        // Remove all device-related params that might exist
        url.searchParams.delete('deviceId');
        url.searchParams.delete('deviceCode');
        url.searchParams.delete('shortSecret');
        url.searchParams.delete('deviceType');

        console.log(
            '[PAIR-APP] URL after clearing device params:',
            url.toString()
        );

        // Update the URL
        window.history.replaceState({}, '', url.toString());
    };

    const handleArtcastSelection = async (queueData?: any) => {
        console.log('[PAIR-APP] PairPage: handleArtcastSelection called');
        console.log('[PAIR-APP] Current user data:', user);
        console.log('[PAIR-APP] Queue data:', queueData);
        console.log('[PAIR-APP] Device params:', { deviceId, deviceCode });

        // Store the queue data for later use
        if (queueData) {
            setSelectedQueueData(queueData);

            // Handle additional gift channels if present
            if (
                queueData.additionalChannels &&
                queueData.additionalChannels.length > 0
            ) {
                console.log(
                    '[PAIR-APP] Processing additional gift channels:',
                    queueData.additionalChannels
                );

                // Subscribe user to gift channels and add artworks to favorites
                try {
                    for (const giftChannel of queueData.additionalChannels) {
                        console.log(
                            '[PAIR-APP] Subscribing to gift channel:',
                            giftChannel.channelId
                        );

                        // Subscribe to the gift channel
                        await api.subscribeToChannel(
                            userId!,
                            giftChannel.channelId
                        );

                        // DISABLED: Gift channel artwork favoriting
                        // Gift artworks should not be automatically added to favorites
                        /*
                        if (
                            giftChannel.artworks &&
                            giftChannel.artworks.length > 0
                        ) {
                            console.log(
                                '[PAIR-APP] Adding gift artworks to favorites:',
                                giftChannel.artworks.length
                            );
                            for (const artwork of giftChannel.artworks) {
                                await api.addToFavorites(
                                    userId!,
                                    artwork.id || artwork.artworkID
                                );
                            }
                        }
                        */
                    }

                    console.log(
                        '[PAIR-APP] Successfully processed all gift channels'
                    );
                } catch (error) {
                    console.error(
                        '[PAIR-APP] Error processing gift channels:',
                        error
                    );
                    // Continue with the flow even if gift subscription fails
                }
            }
        }

        // Check if pairing failed or user wants to pair new device
        if (queueData?.pairingFailed || queueData?.fromAlreadyPaired) {
            console.log(
                '[PAIR-APP] Pairing failed or user wants new device, going to pair_screen'
            );
            // Clear device params since they didn't work or user wants new device
            setDeviceId('');
            setDeviceCode('');
            setCurrentStep('pair_screen');
            return; // Don't proceed with preview pass logic
        }

        // PAYMENT WALL: Check if we should show payment wall before pairing
        // Skip payment wall for users who are already on a paid plan
        if (paymentWallTiming === 'before_pairing' && !isUserOnPaidPlan()) {
            console.log(
                '[PAIR-APP] Payment wall timing: before_pairing - showing payment wall'
            );

            // Use centralized plan selection logic
            const planToOffer = getPlanToOffer();
            setSelectedPlanId(planToOffer);
            setShowPaymentWallModal(true);
            trackPaymentWallConversion('wall_shown', {
                variant: 'before_pairing',
                plan: planToOffer,
            });
            // Don't proceed to pair_screen - payment wall will handle navigation
            return;
        } else if (
            paymentWallTiming === 'before_pairing' &&
            isUserOnPaidPlan()
        ) {
            console.log(
                '[PAIR-APP] Payment wall timing: before_pairing - skipping (user already on paid plan:',
                sessionUser?.plan_display_name,
                ')'
            );
        }

        // Always go to pair_screen after selecting artcast
        // The pair_screen will handle both auto-paired and manual pairing flows
        console.log(
            '[PAIR-APP] Going to pair_screen, auto-paired:',
            !!(deviceId && deviceCode)
        );
        setCurrentStep('pair_screen');
    };

    const handleQRScanSuccess = async (
        scannedDeviceId: string,
        scannedDeviceCode: string
    ) => {
        console.log('[PAIR-APP] PairPage: QR scan successful:', {
            scannedDeviceId,
            scannedDeviceCode,
        });
        setDeviceId(scannedDeviceId);
        setDeviceCode(scannedDeviceCode);
        setIsPairing(true);

        // Call attachDevice to properly pair the device
        if (userId && selectedQueueData) {
            try {
                console.log('[PAIR-APP] Step 1: Attaching device...');
                const pairResult = await api.pairDevice({
                    deviceId: scannedDeviceId,
                    deviceCode: scannedDeviceCode,
                    userId: userId,
                });

                if (pairResult.success) {
                    console.log('[PAIR-APP] Device paired successfully');

                    // Step 2: Set the queue on the device
                    console.log('[PAIR-APP] Step 2: Setting device queue...');
                    const artworksForQueue = (
                        selectedQueueData.artworks || []
                    ).map((artwork: any) => ({
                        artworkID: artwork.id,
                        originalChannelID: selectedQueueData.channelId,
                        downloaded: false,
                    }));

                    const queueResult = await api.setDeviceQueue({
                        deviceId: scannedDeviceId,
                        channelId: selectedQueueData.channelId,
                        artworks: artworksForQueue,
                    });

                    if (queueResult.success) {
                        console.log('[PAIR-APP] Queue set successfully');
                    } else {
                        console.error(
                            '[PAIR-APP] Failed to set queue:',
                            queueResult.error
                        );
                    }

                    // Now that device is attached and queue is set, go to success
                    setIsPairing(false);
                    setCurrentStep('success');
                } else {
                    setIsPairing(false);
                    console.error(
                        '[PAIR-APP] Device pairing failed:',
                        pairResult
                    );
                    console.error('[PAIR-APP] Error details:', {
                        errorType: pairResult?.errorType,
                        error: pairResult?.error,
                        status: pairResult?.status,
                        __isApiError: pairResult?.__isApiError,
                        fullError: pairResult,
                    });

                    // Reset device parameters on failure
                    setDeviceId('');
                    setDeviceCode('');
                    // Clear device params from URL
                    clearDeviceParamsFromURL();

                    // Check if it's a "device already paired" error to show the proper modal
                    // Match the same logic as ArtcastSelection
                    if (
                        pairResult &&
                        pairResult.errorType === 'device_already_paired'
                    ) {
                        console.log(
                            '[PAIR-APP] Device already paired, showing modal'
                        );
                        setShowDeviceAlreadyPairedModal(true);
                        setAlreadyPairedDeviceId(scannedDeviceId);
                    }
                    // Also check for the specific error message when device is already assigned
                    else if (
                        pairResult &&
                        pairResult.error &&
                        typeof pairResult.error === 'string' &&
                        pairResult.error.includes(
                            'Device is already assigned to a user'
                        )
                    ) {
                        console.log(
                            '[PAIR-APP] Device already assigned to a user, showing modal'
                        );
                        setShowDeviceAlreadyPairedModal(true);
                        setAlreadyPairedDeviceId(scannedDeviceId);
                    } else {
                        // For other errors, navigate to pair screen with error flag
                        console.error(
                            '[PAIR-APP] Pairing failed, navigating to pair screen with error flag'
                        );
                        const failureQueueData = {
                            ...selectedQueueData,
                            pairingFailed: true,
                            errorMessage:
                                pairResult?.error || 'Failed to pair device',
                        };
                        setSelectedQueueData(failureQueueData);
                        setCurrentStep('pair_screen');
                    }
                }
            } catch (error: any) {
                setIsPairing(false);
                console.error('[PAIR-APP] Error during device setup:', error);
                console.error('[PAIR-APP] Error details:', {
                    errorType: error?.errorType,
                    error: error?.error,
                    status: error?.status,
                    __isApiError: error?.__isApiError,
                    fullError: error,
                });

                // Reset device parameters on error
                setDeviceId('');
                setDeviceCode('');
                // Clear device params from URL
                clearDeviceParamsFromURL();

                // Check error type - same logic as ArtcastSelection
                if (error && error.errorType === 'device_already_paired') {
                    console.log(
                        '[PAIR-APP] Device already paired, showing modal'
                    );
                    setShowDeviceAlreadyPairedModal(true);
                    setAlreadyPairedDeviceId(scannedDeviceId);
                }
                // Also check for the specific error message when device is already assigned
                else if (
                    error &&
                    error.error &&
                    typeof error.error === 'string' &&
                    error.error.includes('Device is already assigned to a user')
                ) {
                    console.log(
                        '[PAIR-APP] Device already assigned to a user, showing modal'
                    );
                    setShowDeviceAlreadyPairedModal(true);
                    setAlreadyPairedDeviceId(scannedDeviceId);
                } else {
                    // For other errors, navigate to pair screen with error flag
                    console.error(
                        '[PAIR-APP] Pairing failed (catch block), navigating to pair screen with error flag'
                    );
                    const failureQueueData = {
                        ...selectedQueueData,
                        pairingFailed: true,
                        errorMessage:
                            error?.error ||
                            'An error occurred during device pairing',
                    };
                    setSelectedQueueData(failureQueueData);
                    setCurrentStep('pair_screen');
                }
            }
        } else {
            setIsPairing(false);
            console.error(
                '[PAIR-APP] Missing userId or queue data for device pairing'
            );
            // Reset device parameters if prerequisites are missing
            setDeviceId('');
            setDeviceCode('');
            // Clear device params from URL
            clearDeviceParamsFromURL();
            // Navigate to pair screen with error message
            const failureQueueData = {
                ...selectedQueueData,
                pairingFailed: true,
                errorMessage:
                    'Unable to pair device. Please ensure you are logged in and have selected content.',
            };
            setSelectedQueueData(failureQueueData);
            setCurrentStep('pair_screen');
        }
    };

    const handleSaveForLater = () => {
        console.log('[PAIR-APP] Queue saved for later, going to enjoy step');
        setSavedForLater(true);
        setCurrentStep('success');
    };

    const handleAutoPairContinue = () => {
        console.log(
            '[PAIR-APP] Auto-pairing acknowledged, proceeding to success'
        );
        setCurrentStep('success');
    };

    // Handle channel subscription and artwork favoriting when reaching success step
    useEffect(() => {
        // Only proceed if we're on the success step and have user data and queue data
        if (currentStep !== 'success' || !user || !userId || !selectedQueueData)
            return;

        // Skip if already processed
        if (subscriptionComplete) return;

        // Subscribe to channel and add artworks to favorites
        const subscribeAndFavorite = async () => {
            try {
                setIsSubscribingToChannel(true);
                console.log(
                    '[PAIR-APP] Starting channel subscription and favoriting process'
                );

                // Extract channel ID and artworks from queue data
                const channelId =
                    selectedQueueData.channelId || selectedQueueData.channelID;
                const artworks = selectedQueueData.artworks || [];

                if (!channelId) {
                    console.log(
                        '[PAIR-APP] No channel ID found, skipping subscription'
                    );
                    setIsSubscribingToChannel(false);
                    setSubscriptionComplete(true);
                    return;
                }

                // Subscribe to the channel
                console.log('[PAIR-APP] Subscribing to channel:', channelId);
                const subscribeResult = await api.subscribeToChannels([
                    channelId,
                ]);

                if (subscribeResult.success) {
                    console.log(
                        '[PAIR-APP] Successfully subscribed to channel'
                    );
                } else {
                    console.error(
                        '[PAIR-APP] Failed to subscribe to channel:',
                        subscribeResult.error
                    );
                }

                // DISABLED: Artwork favoriting at Enjoy step
                // Artworks should not be automatically added to wishlist
                /*
                if (artworks && artworks.length > 0) {
                    console.log(
                        `[PAIR-APP] Adding ${artworks.length} artworks to favorites`
                    );

                    for (const artwork of artworks) {
                        // Handle both regular artwork objects and saved queue format
                        const artworkId = artwork.id || artwork.artworkID;

                        if (artworkId) {
                            try {
                                await api.toggleWishlist(artworkId);
                                console.log(
                                    `[PAIR-APP] Added artwork ${artworkId} to favorites`
                                );
                            } catch (error) {
                                console.error(
                                    `[PAIR-APP] Failed to add artwork ${artworkId} to favorites:`,
                                    error
                                );
                            }
                        }
                    }

                    console.log(
                        '[PAIR-APP] Finished adding artworks to favorites'
                    );
                } else {
                    console.log('[PAIR-APP] No artworks to add to favorites');
                }
                */

                setSubscriptionComplete(true);
            } catch (error) {
                console.error(
                    '[PAIR-APP] Error during subscription/favoriting process:',
                    error
                );
                setSubscriptionComplete(true);
            } finally {
                setIsSubscribingToChannel(false);
            }
        };

        // Execute the subscription and favoriting
        subscribeAndFavorite();
    }, [currentStep, user, userId, selectedQueueData, subscriptionComplete]);

    // PAYMENT WALL: Handle payment wall display for "after_pairing" variant
    useEffect(() => {
        // Only proceed if we're on the success step and have user data
        if (currentStep !== 'success' || !user || !userId) return;

        // Skip payment wall for users who are already on a paid plan
        if (isUserOnPaidPlan()) {
            console.log(
                '[PAIR-APP] Payment wall: skipping (user already on paid plan:',
                sessionUser?.plan_display_name,
                ')'
            );
            setShowDownloadButton(true);
            return;
        }

        // Check A/B test variant - only show payment wall in "after_pairing" variant
        if (paymentWallTiming === 'after_pairing') {
            console.log(
                '[PAIR-APP] Payment wall timing: after_pairing - showing payment wall'
            );

            // Delay showing payment wall by 2.5 seconds to let user celebrate
            const showWallTimer = setTimeout(() => {
                // Use centralized plan selection logic
                const planToOffer = getPlanToOffer();
                setSelectedPlanId(planToOffer);
                setShowPaymentWallModal(true);
                trackPaymentWallConversion('wall_shown', {
                    variant: 'after_pairing',
                    plan: planToOffer,
                });
            }, 2500);

            return () => clearTimeout(showWallTimer);
        } else {
            // For "before_pairing" variant, show download button immediately
            console.log(
                '[PAIR-APP] Payment wall timing: before_pairing - showing download button'
            );
            setShowDownloadButton(true);
        }
    }, [
        currentStep,
        user,
        userId,
        paymentWallTiming,
        couponData,
        userAccountType,
        isUserOnPaidPlan,
        sessionUser?.plan_display_name,
    ]);

    return (
        <>
            <div className="min-h-screen relative overflow-x-hidden w-full">
                <Background />

                {/* Floating decorative elements - hide on very small screens */}
                <div className="fixed inset-0 pointer-events-none z-5 hidden xs:block">
                    <motion.div
                        className="absolute top-20 left-20 w-2 h-2 bg-purple-500 rounded-full opacity-60"
                        animate={{
                            y: [-10, 10, -10],
                            opacity: [0.3, 0.8, 0.3],
                        }}
                        transition={{
                            duration: 3,
                            repeat: Infinity,
                            ease: 'easeInOut',
                        }}
                    />
                    <motion.div
                        className="absolute top-40 right-32 w-1 h-1 bg-pink-500 rounded-full opacity-60"
                        animate={{ y: [10, -10, 10], opacity: [0.4, 0.9, 0.4] }}
                        transition={{
                            duration: 2.5,
                            repeat: Infinity,
                            ease: 'easeInOut',
                            delay: 0.5,
                        }}
                    />
                    <motion.div
                        className="absolute bottom-32 left-40 w-3 h-3 bg-blue-500 rounded-full opacity-50"
                        animate={{
                            y: [-15, 15, -15],
                            opacity: [0.2, 0.7, 0.2],
                        }}
                        transition={{
                            duration: 4,
                            repeat: Infinity,
                            ease: 'easeInOut',
                            delay: 1,
                        }}
                    />
                    <motion.div
                        className="absolute bottom-20 right-20 w-1.5 h-1.5 bg-green-500 rounded-full opacity-50"
                        animate={{ y: [8, -8, 8], opacity: [0.3, 0.8, 0.3] }}
                        transition={{
                            duration: 3.5,
                            repeat: Infinity,
                            ease: 'easeInOut',
                            delay: 1.5,
                        }}
                    />
                </div>

                {/* Welcome Back Page - shown for device_already_paired instead of modal */}
                {welcomeModalVariant === 'device_already_paired' &&
                    sessionUser && (
                        <WelcomeBackPage
                            firstName={
                                sessionUser?.name?.split(' ')[0] ||
                                sessionUser?.first_name
                            }
                            isUpgraded={isPaidPlan()}
                            onStartTrial={() => {
                                // Same logic as upgrade from menu
                                setUpgradeFromMenu(true);
                                const planToOffer = getPlanToOffer();
                                setSelectedPlanId(planToOffer);
                                trackPaymentWallConversion(
                                    'upgrade_from_device_paired',
                                    {
                                        plan: planToOffer,
                                    }
                                );
                                setShowPaymentWallModal(true);
                            }}
                            onDownloadApp={() => {
                                // Use the default onelink for app download
                                const baseUrl =
                                    couponData?.onelink ||
                                    'https://niioart.onelink.me/QVcR/artgift';
                                const params = new URLSearchParams();
                                if (sessionUser?.id) {
                                    params.append('userId', sessionUser.id);
                                }
                                params.append('newUser', 'true');
                                const downloadUrl = `${baseUrl}?${params.toString()}`;
                                window.open(downloadUrl, '_blank');
                            }}
                            onLogout={() => {
                                // Reset everything when user logs out
                                setCurrentStep('email');
                                setUserEmail('');
                                setUserId(null);
                                setUser(null);
                                setSessionUser(null);
                                setSelectedChannel(null);
                                setAiSearchedChannels(null);
                                setAiDescription('');
                                setAiGenres([]);
                                setIsViewingSavedQueue(false);
                                setSavedQueue(null);
                                setWelcomeModalVariant('new_user');
                            }}
                            onUpgrade={() => {
                                // Use centralized plan selection logic
                                const planToOffer = getPlanToOffer();
                                setSelectedPlanId(planToOffer);
                                setUpgradeFromMenu(true);
                                trackPaymentWallConversion(
                                    'upgrade_from_menu',
                                    {
                                        plan: planToOffer,
                                    }
                                );
                                setShowPaymentWallModal(true);
                            }}
                        />
                    )}

                {/* Normal wizard flow - hidden when showing WelcomeBackPage */}
                {!(
                    welcomeModalVariant === 'device_already_paired' &&
                    sessionUser
                ) && (
                    <div className="relative z-10 min-h-screen flex flex-col w-full">
                        {currentStep !== 'email' && (
                            <ProgressIndicator
                                currentStep={getAdjustedStepNumber(currentStep)}
                                onLogout={() => {
                                    // Reset to step 0 (email/connect step) when user logs out
                                    setCurrentStep('email');
                                    // Clear all user-related state
                                    setUserEmail('');
                                    setUserId(null);
                                    setUser(null);
                                    setSelectedChannel(null);
                                    setAiSearchedChannels(null);
                                    setAiDescription('');
                                    setAiGenres([]);
                                    setIsViewingSavedQueue(false);
                                    setSavedQueue(null);
                                }}
                                onUpgrade={() => {
                                    // Use centralized plan selection logic
                                    const planToOffer = getPlanToOffer();
                                    setSelectedPlanId(planToOffer);
                                    // Mark that upgrade was opened from menu
                                    // so closing modals won't redirect the user
                                    setUpgradeFromMenu(true);
                                    trackPaymentWallConversion(
                                        'upgrade_from_menu',
                                        {
                                            plan: planToOffer,
                                        }
                                    );
                                    setShowPaymentWallModal(true);
                                }}
                            />
                        )}

                        <div
                            className={`flex-1 flex ${
                                currentStep === 'artcast'
                                    ? 'items-start pt-12 sm:pt-16'
                                    : 'items-center'
                            } justify-center px-4 sm:px-6 pb-20 w-full`}
                        >
                            <motion.div
                                className="w-full max-w-mobile sm:max-w-desktop-form mx-auto"
                                initial={{ opacity: 0, scale: 0.9 }}
                                animate={{ opacity: 1, scale: 1 }}
                                transition={{ duration: 0.8, ease: 'easeOut' }}
                            >
                                <motion.div
                                    className="text-center mb-8"
                                    initial={{ opacity: 0, y: -30 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.6, delay: 0.2 }}
                                >
                                    {currentStep === 'email' && (
                                        <>
                                            {/* Niio Logo with animated purple glow - always shown */}
                                            <motion.div
                                                className="relative inline-block mb-6 mt-0"
                                                initial={{
                                                    scale: 0.8,
                                                    opacity: 0,
                                                }}
                                                animate={{
                                                    scale: 1,
                                                    opacity: 1,
                                                }}
                                                transition={{
                                                    duration: 0.8,
                                                    ease: 'easeOut',
                                                }}
                                            >
                                                {/* Purple glow background (animated) - behind TV */}
                                                <motion.div
                                                    className="absolute bg-purple-500/60 rounded-full blur-3xl"
                                                    animate={{
                                                        scale: [1, 1.2, 1],
                                                        opacity: [
                                                            0.6, 0.9, 0.6,
                                                        ],
                                                    }}
                                                    transition={{
                                                        duration: 3,
                                                        repeat: Infinity,
                                                        ease: 'easeInOut',
                                                    }}
                                                    style={{
                                                        transform:
                                                            'translate(-50%, -50%)',
                                                        width: '150%',
                                                        height: '150%',
                                                        zIndex: 1,
                                                    }}
                                                />
                                                {/* White glow foreground (static) - behind TV */}
                                                <div
                                                    className="absolute bg-white/20 rounded-full blur-2xl"
                                                    style={{
                                                        transform:
                                                            'translate(-50%, -50%)',
                                                        top: '50%',
                                                        left: '50%',
                                                        width: '140%',
                                                        height: '140%',
                                                        zIndex: 2,
                                                    }}
                                                />

                                                {/* TV Screen - appears after 3 seconds */}
                                                <TVScreen show={showTVScreen} />

                                                {/* Logo - inside TV screen when TV is shown */}
                                                {/* Using SVG here for proper scaling in animated TV screen */}
                                                <motion.img
                                                    src="/niio_logo_white.svg"
                                                    alt="Niio"
                                                    className="relative w-48 h-48"
                                                    animate={{
                                                        scale: showTVScreen
                                                            ? 0.7
                                                            : 1,
                                                        y: showTVScreen ? 0 : 0,
                                                    }}
                                                    transition={{
                                                        duration: 0.8,
                                                        ease: 'easeInOut',
                                                    }}
                                                    style={{
                                                        filter: 'drop-shadow(0 10px 40px rgba(139, 92, 246, 0.7)) drop-shadow(0 4px 20px rgba(139, 92, 246, 0.5))',
                                                        backgroundColor:
                                                            'transparent',
                                                        isolation: 'isolate',
                                                        zIndex: 10,
                                                    }}
                                                />
                                            </motion.div>

                                            <h1 className="text-4xl font-bold text-white mb-3 mt-0">
                                                <motion.span className="text-white">
                                                    Pair Your Screen
                                                </motion.span>
                                            </h1>
                                        </>
                                    )}

                                    {currentStep === 'artcast' && (
                                        <h1 className="text-4xl font-bold text-white mb-3">
                                            <motion.span className="text-white">
                                                {selectedChannel
                                                    ? 'Great Choice!'
                                                    : isViewingSavedQueue
                                                    ? 'Your Saved Art'
                                                    : aiSearchedChannels
                                                    ? "We've matched artcasts we think you'll love."
                                                    : giftChannels
                                                    ? 'Your Gift Collection'
                                                    : 'Select Your Artcast'}
                                            </motion.span>
                                        </h1>
                                    )}

                                    {currentStep === 'pair_screen' && (
                                        <h1 className="text-4xl font-bold text-white mb-3">
                                            <motion.span className="text-white">
                                                {deviceId && deviceCode
                                                    ? 'Screen Paired!'
                                                    : 'Pair with Screen'}
                                            </motion.span>
                                        </h1>
                                    )}

                                    {currentStep === 'success' && (
                                        <h1 className="text-4xl font-bold text-white mb-3">
                                            <motion.span className="text-white">
                                                You're All Set!
                                            </motion.span>
                                        </h1>
                                    )}

                                    <motion.p
                                        className="text-gray-400 text-lg"
                                        animate={{ opacity: [0.7, 1, 0.7] }}
                                        transition={{
                                            duration: 2,
                                            repeat: Infinity,
                                            ease: 'easeInOut',
                                        }}
                                    >
                                        {currentStep === 'email' &&
                                            'Enter your details to get started'}
                                        {currentStep === 'artcast' &&
                                            (selectedChannel
                                                ? "You've selected an amazing artcast"
                                                : isViewingSavedQueue
                                                ? "Here's the art you saved from your last visit"
                                                : aiSearchedChannels
                                                ? 'Select an artcast to begin'
                                                : giftChannels
                                                ? 'A special artcast gifted to you'
                                                : 'Select an artcast to begin')}
                                        {currentStep === 'pair_screen' &&
                                            'Scan QR code or save for later'}
                                        {currentStep === 'success' &&
                                            'Enjoy curated digital art'}
                                    </motion.p>
                                </motion.div>

                                <AnimatePresence mode="wait">
                                    {currentStep === 'email' && (
                                        <div className="bg-gray-900/90 backdrop-blur-xl rounded-2xl border border-gray-600 p-4 sm:p-6 md:p-8 shadow-2xl w-full">
                                            <motion.div
                                                key="email"
                                                initial={{
                                                    opacity: 0,
                                                    scale: 0.95,
                                                }}
                                                animate={{
                                                    opacity: 1,
                                                    scale: 1,
                                                }}
                                                exit={{
                                                    opacity: 0,
                                                    scale: 0.95,
                                                }}
                                                transition={{
                                                    duration: 0.3,
                                                    ease: [0.4, 0, 0.2, 1],
                                                }}
                                            >
                                                {isCheckingSession ? (
                                                    <div className="flex flex-col items-center justify-center py-8">
                                                        <div className="w-8 h-8 border-2 border-purple-500 border-t-transparent rounded-full animate-spin mb-4"></div>
                                                        <p className="text-gray-400 text-center">
                                                            Checking your
                                                            session...
                                                        </p>
                                                    </div>
                                                ) : (
                                                    <PairFormSocial
                                                        deviceId={deviceId}
                                                        deviceCode={deviceCode}
                                                        fromCampaign={
                                                            fromCampaign
                                                        }
                                                        onSuccess={
                                                            handleEmailSubmit
                                                        }
                                                        shouldDisableAutoFocus={
                                                            showAIPersonalizationModal ||
                                                            showWelcomeModal ||
                                                            showAccountTypeModal ||
                                                            showPaymentWallModal ||
                                                            showBillingModal ||
                                                            showCampaignModal
                                                        }
                                                    />
                                                )}
                                            </motion.div>
                                        </div>
                                    )}

                                    {currentStep === 'artcast' && (
                                        <ArtcastSelection
                                            key="artcast"
                                            userEmail={userEmail}
                                            userId={userId!}
                                            deviceId={deviceId}
                                            deviceCode={deviceCode}
                                            onSuccess={handleArtcastSelection}
                                            onSelectionChange={
                                                setSelectedChannel
                                            }
                                            savedQueueData={
                                                isViewingSavedQueue
                                                    ? savedQueue
                                                    : undefined
                                            }
                                            isViewingSavedQueue={
                                                isViewingSavedQueue
                                            }
                                            onStartFresh={
                                                handleStartFreshFromArtcast
                                            }
                                            aiSearchedChannels={
                                                aiSearchedChannels
                                            }
                                            giftChannels={giftChannels}
                                            isAIModalOpen={
                                                showAIPersonalizationModal
                                            }
                                            onBackToStart={() => {
                                                console.log(
                                                    '[PAIR-APP] Resetting to step 1 from ArtcastSelection'
                                                );
                                                setCurrentStep('email');
                                                setSelectedChannel(null);
                                                setAiSearchedChannels(null); // Clear AI searched channels
                                                setAiDescription('');
                                                setAiGenres([]);
                                                // Clear the URL query params
                                                window.history.replaceState(
                                                    {},
                                                    '',
                                                    window.location.pathname
                                                );
                                                // Clear device info
                                                setDeviceId('');
                                                setDeviceCode('');
                                            }}
                                        />
                                    )}

                                    {currentStep === 'pair_screen' && (
                                        <div className="bg-gray-900/90 backdrop-blur-xl rounded-2xl border border-gray-600 p-4 sm:p-6 md:p-8 shadow-2xl w-full">
                                            <motion.div
                                                key="pair_screen"
                                                initial={{
                                                    opacity: 0,
                                                    scale: 0.95,
                                                }}
                                                animate={{
                                                    opacity: 1,
                                                    scale: 1,
                                                }}
                                                exit={{
                                                    opacity: 0,
                                                    scale: 0.95,
                                                }}
                                                transition={{
                                                    duration: 0.3,
                                                    ease: [0.4, 0, 0.2, 1],
                                                }}
                                            >
                                                <PairWithScreen
                                                    onQRScan={
                                                        handleQRScanSuccess
                                                    }
                                                    onSaveForLater={
                                                        handleSaveForLater
                                                    }
                                                    queueData={
                                                        selectedQueueData
                                                    }
                                                    userId={userId!}
                                                    isAutoPaired={
                                                        !!(
                                                            deviceId &&
                                                            deviceCode
                                                        )
                                                    }
                                                    onContinue={
                                                        deviceId && deviceCode
                                                            ? handleAutoPairContinue
                                                            : undefined
                                                    }
                                                    isPairing={isPairing}
                                                />
                                            </motion.div>
                                        </div>
                                    )}

                                    {currentStep === 'success' && (
                                        <div className="bg-gray-900/90 backdrop-blur-xl rounded-2xl border border-gray-600 p-4 sm:p-6 md:p-8 shadow-2xl w-full">
                                            <motion.div
                                                key="success"
                                                initial={{
                                                    opacity: 0,
                                                    scale: 0.95,
                                                }}
                                                animate={{
                                                    opacity: 1,
                                                    scale: 1,
                                                }}
                                                exit={{
                                                    opacity: 0,
                                                    scale: 0.95,
                                                }}
                                                transition={{
                                                    duration: 0.3,
                                                    ease: [0.4, 0, 0.2, 1],
                                                }}
                                            >
                                                <PairingSuccess
                                                    userEmail={userEmail}
                                                    userId={userId || ''}
                                                    showDownloadButton={
                                                        showDownloadButton
                                                    }
                                                    savedForLater={
                                                        savedForLater
                                                    }
                                                    couponData={couponData}
                                                    previewPassPending={
                                                        false /* PAYMENT WALL: Always false now */
                                                    }
                                                    isSubscribingToChannel={
                                                        isSubscribingToChannel
                                                    }
                                                    subscriptionComplete={
                                                        subscriptionComplete
                                                    }
                                                    disableCelebration={
                                                        showPaymentWallModal ||
                                                        showLastChanceModal ||
                                                        showBillingModal
                                                    }
                                                />
                                            </motion.div>
                                        </div>
                                    )}
                                </AnimatePresence>
                            </motion.div>
                        </div>
                    </div>
                )}

                {/* PAYMENT WALL: Preview Pass Modal removed - replaced with Payment Wall
                <PreviewPassModal
                    isOpen={showPreviewPassModal}
                    onClose={() => {
                        setShowPreviewPassModal(false);
                        setShowDownloadButton(true);
                    }}
                    daysCount={user?.preview_pass_x_days || 0}
                    endDate={previewPassEndDate || new Date()}
                />
                */}

                {/* Payment Wall Modal */}
                <PaymentWallModal
                    isOpen={showPaymentWallModal}
                    onClose={() => {
                        // Track that user closed the payment wall
                        trackPaymentWallConversion('payment_wall_closed', {
                            plan: selectedPlanId,
                        });
                        // Show LastChance modal instead of just closing
                        setShowPaymentWallModal(false);
                        setShowLastChanceModal(true);
                    }}
                    onUpgrade={() => {
                        trackPaymentWallConversion('upgrade_clicked', {
                            plan: selectedPlanId,
                        });
                        setShowPaymentWallModal(false);
                        setShowBillingModal(true);
                    }}
                    onContinueFree={() => {
                        // Track that user clicked continue free
                        trackPaymentWallConversion('continue_free_clicked', {
                            plan: selectedPlanId,
                        });
                        // Show LastChance modal instead of continuing free immediately
                        setShowPaymentWallModal(false);
                        setShowLastChanceModal(true);
                    }}
                    variant={
                        paymentWallTiming as 'before_pairing' | 'after_pairing'
                    }
                    artcastName={
                        selectedQueueData?.channelData?.name ||
                        selectedQueueData?.channelName ||
                        'your artcast'
                    }
                    planName={
                        selectedPlanId?.toLowerCase().includes('pro')
                            ? 'Pro'
                            : selectedPlanId?.toLowerCase().includes('plus')
                            ? 'Plus'
                            : 'Standard'
                    }
                    accountType={userAccountType}
                />

                {/* Last Chance Modal - shown when user tries to continue free or closes PaymentWall */}
                <LastChanceModal
                    isOpen={showLastChanceModal}
                    onClose={() => {
                        // Track that user closed the last chance modal
                        trackPaymentWallConversion('last_chance_closed', {
                            plan: selectedPlanId,
                            accountType: userAccountType,
                        });
                        setShowLastChanceModal(false);
                        // If opened from menu (including from WelcomeBackPage), just close - don't redirect
                        // WelcomeBackPage renders automatically when variant is device_already_paired
                        if (upgradeFromMenu) {
                            setUpgradeFromMenu(false);
                            return;
                        }
                        // Continue with original flow
                        if (paymentWallTiming === 'before_pairing') {
                            setCurrentStep('pair_screen');
                        } else {
                            setShowDownloadButton(true);
                        }
                    }}
                    onUpgrade={() => {
                        // Track that user decided to upgrade from last chance
                        trackPaymentWallConversion(
                            'last_chance_upgrade_clicked',
                            {
                                plan: selectedPlanId,
                                accountType: userAccountType,
                            }
                        );
                        setShowLastChanceModal(false);
                        setShowBillingModal(true);
                    }}
                    onContinueFree={() => {
                        // Track that user confirmed continuing with free plan
                        trackPaymentWallConversion(
                            'last_chance_continued_free',
                            {
                                plan: selectedPlanId,
                                accountType: userAccountType,
                            }
                        );
                        setShowLastChanceModal(false);
                        // If opened from menu (including from WelcomeBackPage), just close - don't redirect
                        // WelcomeBackPage renders automatically when variant is device_already_paired
                        if (upgradeFromMenu) {
                            setUpgradeFromMenu(false);
                            return;
                        }
                        // For "before_pairing" variant, continue to pair_screen
                        // For "after_pairing" variant, show download button
                        if (paymentWallTiming === 'before_pairing') {
                            setCurrentStep('pair_screen');
                        } else {
                            setShowDownloadButton(true);
                        }
                    }}
                    accountType={userAccountType}
                    planName={
                        userAccountType === 'business' ? 'Starter' : 'Basic'
                    }
                />

                {/* Billing Modal (iframe) */}
                <BillingModal
                    isOpen={showBillingModal}
                    onClose={() => {
                        setShowBillingModal(false);
                        // If opened from menu (including from WelcomeBackPage), just close - don't redirect
                        // WelcomeBackPage renders automatically when variant is device_already_paired
                        if (upgradeFromMenu) {
                            setUpgradeFromMenu(false);
                            return;
                        }
                        // For "before_pairing" variant, continue to pair_screen
                        // For "after_pairing" variant, show download button
                        if (paymentWallTiming === 'before_pairing') {
                            setCurrentStep('pair_screen');
                        } else {
                            setShowDownloadButton(true);
                        }
                    }}
                    onSuccess={async (planDisplayName?: string) => {
                        trackPaymentWallConversion('payment_completed', {
                            plan: selectedPlanId,
                        });
                        setShowBillingModal(false);
                        // If opened from menu, just close - don't redirect
                        // (user data refresh still happens below)
                        const wasFromMenu = upgradeFromMenu;
                        if (wasFromMenu) {
                            setUpgradeFromMenu(false);
                        }
                        // OPTIMISTIC UPDATE: Immediately show the plan the user just subscribed to
                        // This provides instant UI feedback before the backend processes the webhook
                        // NOTE: Do this BEFORE device_already_paired check so isPaidPlan() returns true
                        if (planDisplayName && sessionUser) {
                            console.log(
                                '[PAIR-APP] Optimistic update - setting plan to:',
                                planDisplayName
                            );
                            setSessionUser({
                                ...sessionUser,
                                plan_display_name: planDisplayName,
                            });
                        }

                        // WelcomeBackPage renders automatically when variant is device_already_paired
                        // It will now show without trial button since isPaidPlan() is true after upgrade

                        // For regular flow, continue to next step
                        if (!wasFromMenu) {
                            // For "before_pairing" variant, continue to pair_screen
                            // For "after_pairing" variant, show download button
                            if (paymentWallTiming === 'before_pairing') {
                                setCurrentStep('pair_screen');
                            } else {
                                setShowDownloadButton(true);
                            }
                        }

                        // Refresh user data from server to reflect subscription
                        // Wait for Chargebee webhook to be processed by backend
                        if (sessionUser) {
                            try {
                                console.log(
                                    '[PAIR-APP] Waiting for backend to process subscription...'
                                );
                                // Give backend time to process Chargebee webhook
                                await new Promise((resolve) =>
                                    setTimeout(resolve, 5000)
                                );

                                console.log(
                                    '[PAIR-APP] Refreshing user data after subscription...'
                                );
                                const userDetailsResult =
                                    await api.getUserDetails(sessionUser.id);
                                if (
                                    userDetailsResult.success &&
                                    userDetailsResult.data
                                ) {
                                    // The API returns nested data - extract the actual user data
                                    // It could be under data.data, data.User, or directly in data
                                    const responseData = userDetailsResult.data;
                                    const actualUserData =
                                        responseData.data ||
                                        responseData.User ||
                                        responseData;

                                    console.log(
                                        '[PAIR-APP] Fresh user data after subscription:',
                                        actualUserData
                                    );

                                    // Merge with session user, preserving session token and ID
                                    const updatedUser = {
                                        ...sessionUser,
                                        ...actualUserData,
                                        id: sessionUser.id, // Preserve session ID
                                        token: sessionUser.token, // Preserve session token
                                    };
                                    console.log(
                                        '[PAIR-APP] Updated user data:',
                                        updatedUser
                                    );
                                    setSessionUser(updatedUser);
                                }
                            } catch (err) {
                                console.error(
                                    '[PAIR-APP] Failed to refresh user data:',
                                    err
                                );
                            }
                        }
                    }}
                    planId={selectedPlanId || 'plus'}
                    couponCode={originalCouponCode || undefined}
                    accountType={userAccountType}
                    provider={
                        couponData?.provider as
                            | 'apple'
                            | 'charge_bee'
                            | undefined
                    }
                    onVerifySubscription={async () => {
                        // Verify if payment actually succeeded by checking subscription status
                        // This is called when Google Pay SDK hangs due to browser extensions
                        if (!sessionUser?.id) {
                            console.log(
                                '[PAIR-APP] Cannot verify - no session user'
                            );
                            return false;
                        }
                        try {
                            console.log(
                                '[PAIR-APP] Verifying subscription status after Google Pay timeout...'
                            );
                            const userDetailsResult = await api.getUserDetails(
                                sessionUser.id
                            );
                            if (
                                userDetailsResult.success &&
                                userDetailsResult.data
                            ) {
                                const responseData = userDetailsResult.data;
                                const actualUserData =
                                    responseData.data ||
                                    responseData.User ||
                                    responseData;
                                const planDisplayName =
                                    actualUserData.plan_display_name;
                                console.log(
                                    '[PAIR-APP] Current plan_display_name:',
                                    planDisplayName
                                );

                                // Payment succeeded if user is no longer on free plan
                                if (
                                    planDisplayName &&
                                    planDisplayName.toLowerCase() !== 'free'
                                ) {
                                    console.log(
                                        '[PAIR-APP] Payment verified - user now on paid plan:',
                                        planDisplayName
                                    );
                                    // Update session user with new plan
                                    setSessionUser({
                                        ...sessionUser,
                                        ...actualUserData,
                                        plan_display_name: planDisplayName,
                                    });
                                    return true;
                                }
                            }
                            console.log(
                                '[PAIR-APP] Payment not verified - user still on free plan'
                            );
                            return false;
                        } catch (err) {
                            console.error(
                                '[PAIR-APP] Subscription verification failed:',
                                err
                            );
                            return false;
                        }
                    }}
                    onPaymentFailed={(error, paymentMethod) => {
                        trackPaymentWallConversion('payment_failed', {
                            plan: selectedPlanId,
                            error,
                            payment_method: paymentMethod,
                        });
                    }}
                    onCouponApplied={(couponCode) => {
                        trackPaymentWallConversion('coupon_applied', {
                            plan: selectedPlanId,
                            coupon_code: couponCode,
                        });
                    }}
                    onCouponFailed={(couponCode, error) => {
                        trackPaymentWallConversion('coupon_failed', {
                            plan: selectedPlanId,
                            coupon_code: couponCode,
                            error,
                        });
                    }}
                    onPaymentMethodSelected={(method) => {
                        trackPaymentWallConversion('payment_method_selected', {
                            plan: selectedPlanId,
                            payment_method: method,
                        });
                    }}
                />

                {/* Account Type Modal */}
                <AccountTypeModal
                    isOpen={showAccountTypeModal}
                    onClose={handleAccountTypeSelection}
                />

                {/* Unified Welcome Modal */}
                <UnifiedWelcomeModal
                    isOpen={showWelcomeModal}
                    onClose={() => {
                        setShowWelcomeModal(false);
                        // Don't trigger any action on close - let the buttons handle their own actions
                        // This prevents the AI processing modal from showing when "Use saved art" is clicked
                        console.log('[PAIR-APP] Welcome modal closed');
                    }}
                    onUseSaved={
                        welcomeModalVariant === 'returning_with_saved'
                            ? handleWelcomeUseSaved
                            : undefined
                    }
                    onStartFresh={handleWelcomeStartFresh}
                    firstName={user?.first_name || sessionUser?.first_name}
                    variant={welcomeModalVariant}
                    hasDeviceParams={!!(deviceId && deviceCode)}
                    // Props for device_already_paired variant
                    // Use shared isPaidPlan() from context for DRY (same as UserAvatar menu)
                    isUpgraded={isPaidPlan()}
                    onStartTrial={() => {
                        setShowWelcomeModal(false);
                        // Same logic as upgrade from menu
                        setUpgradeFromMenu(true);
                        const planToOffer = getPlanToOffer();
                        setSelectedPlanId(planToOffer);
                        trackPaymentWallConversion(
                            'upgrade_from_device_paired',
                            {
                                plan: planToOffer,
                            }
                        );
                        setShowPaymentWallModal(true);
                    }}
                    onDownloadApp={() => {
                        // Track app download click
                        analytics.trackEvent(
                            AnalyticsEvents.APP_DOWNLOAD_CLICKED,
                            {
                                location: 'unified_welcome_modal',
                                variant: 'device_already_paired',
                                user_id: sessionUser?.id,
                            }
                        );
                        // Use the default onelink for app download
                        const baseUrl =
                            couponData?.onelink ||
                            'https://niioart.onelink.me/QVcR/artgift';
                        const params = new URLSearchParams();
                        if (sessionUser?.id) {
                            params.append('userId', sessionUser.id);
                        }
                        params.append('newUser', 'true');
                        const downloadUrl = `${baseUrl}?${params.toString()}`;
                        window.open(downloadUrl, '_blank');
                    }}
                />

                {/* Already Paired Modal */}
                <AlreadyPairedModal
                    isOpen={showDeviceAlreadyPairedModal}
                    deviceId={alreadyPairedDeviceId}
                    onBackToStart={() => {
                        setShowDeviceAlreadyPairedModal(false);
                        // Clear the URL query params and reset
                        clearDeviceParamsFromURL();
                        setCurrentStep('pair_screen');
                    }}
                    onPairNewDevice={() => {
                        setShowDeviceAlreadyPairedModal(false);
                        // Stay on pair screen to try another device
                        setCurrentStep('pair_screen');
                    }}
                />

                {/* Campaign Modal */}
                {couponData && (
                    <CampaignModal
                        isOpen={showCampaignModal}
                        couponData={couponData}
                        onClose={async () => {
                            setShowCampaignModal(false);
                            setHasShownModal(true); // Prevent modal from reopening

                            // If user is logged in after closing campaign modal, check for saved queue
                            if (sessionUser && currentStep === 'email') {
                                console.log(
                                    '[PAIR-APP] User logged in after campaign modal, checking for saved queue'
                                );

                                setIsCheckingSession(true);

                                // Associate coupon with user when campaign modal closes
                                if (
                                    couponData &&
                                    originalCouponCode &&
                                    couponData.provider
                                ) {
                                    console.log(
                                        '[PAIR-APP] Associating coupon after campaign modal close:',
                                        {
                                            provider: couponData.provider,
                                            couponCode: originalCouponCode,
                                        }
                                    );

                                    try {
                                        const associateResult =
                                            await api.associateCouponForUser(
                                                couponData.provider as
                                                    | 'apple'
                                                    | 'charge_bee',
                                                originalCouponCode
                                            );

                                        if (associateResult.success) {
                                            console.log(
                                                '[PAIR-APP] Successfully associated coupon after campaign modal'
                                            );
                                        } else {
                                            console.error(
                                                '[PAIR-APP] Failed to associate coupon:',
                                                associateResult.error
                                            );
                                        }
                                    } catch (error) {
                                        console.error(
                                            '[PAIR-APP] Error associating coupon:',
                                            error
                                        );
                                        // Continue with the flow even if coupon association fails
                                    }
                                }

                                // Check for saved queue for returning users
                                try {
                                    // First, try to get fresh user details
                                    try {
                                        const userDetailsResult =
                                            await api.getUserDetails(
                                                sessionUser.id
                                            );
                                        if (
                                            userDetailsResult.success &&
                                            userDetailsResult.data
                                        ) {
                                            console.log(
                                                '[PAIR-APP] Fresh user details fetched after campaign:',
                                                userDetailsResult.data
                                            );

                                            // The API returns nested data - extract the actual user data
                                            // It could be under data.data, data.User, or directly in data
                                            const responseData =
                                                userDetailsResult.data;
                                            const actualUserData =
                                                responseData.data ||
                                                responseData.User ||
                                                responseData;

                                            console.log(
                                                '[PAIR-APP] Extracted user data after campaign:',
                                                actualUserData
                                            );

                                            // Merge with session user, ensuring we preserve the session token and ID
                                            const freshUserData = {
                                                ...sessionUser,
                                                ...actualUserData,
                                                id: sessionUser.id, // Preserve session ID
                                                token: sessionUser.token, // Preserve session token
                                            };

                                            console.log(
                                                '[PAIR-APP] Final merged user data after campaign:',
                                                freshUserData
                                            );
                                            setUser(freshUserData);
                                            setSessionUser(freshUserData);
                                        }
                                    } catch (error) {
                                        console.log(
                                            '[PAIR-APP] Could not fetch fresh user details after campaign, using cached data'
                                        );
                                    }

                                    const savedQueueResult =
                                        await api.getQueueForLater();
                                    console.log(
                                        '[PAIR-APP] Saved queue API response after campaign:',
                                        savedQueueResult
                                    );

                                    if (
                                        savedQueueResult.success &&
                                        savedQueueResult.data
                                    ) {
                                        console.log(
                                            '[PAIR-APP] Found saved queue after campaign modal:',
                                            savedQueueResult.data
                                        );
                                        console.log(
                                            '[PAIR-APP] User data when showing modal:',
                                            sessionUser,
                                            user
                                        );
                                        setSavedQueue(savedQueueResult.data);

                                        // Check if user has a new coupon that needs to be shown
                                        if (
                                            couponData &&
                                            couponData.is_exists_for_user ===
                                                false
                                        ) {
                                            console.log(
                                                '[PAIR-APP] User has a new coupon after campaign modal, showing surprise gift modal instead of saved queue'
                                            );
                                            // Check if we have a gift channel from the campaign
                                            if (hasGiftChannels(couponData)) {
                                                setGiftRevealed(false); // Reset the flag when showing the modal
                                                setShowSurpriseGiftModal(true);
                                            } else {
                                                // Show AI personalization modal for new coupon without gift
                                                setShowAIPersonalizationModal(
                                                    true
                                                );
                                            }
                                        } else {
                                            setWelcomeModalVariant(
                                                'returning_with_saved'
                                            );
                                            setShowWelcomeModal(true);
                                        }
                                        // Stay on email step to show the modal
                                    } else {
                                        console.log(
                                            '[PAIR-APP] No saved queue found after campaign modal'
                                        );

                                        // Check if user has a new coupon that needs to be shown
                                        if (
                                            couponData &&
                                            couponData.is_exists_for_user ===
                                                false
                                        ) {
                                            console.log(
                                                '[PAIR-APP] User has a new coupon after campaign modal, showing surprise gift modal instead of welcome back'
                                            );
                                            // Check if we have a gift channel from the campaign
                                            if (hasGiftChannels(couponData)) {
                                                setGiftRevealed(false); // Reset the flag when showing the modal
                                                setShowSurpriseGiftModal(true);
                                            } else {
                                                // Show AI personalization modal for new coupon without gift
                                                setShowAIPersonalizationModal(
                                                    true
                                                );
                                            }
                                        } else {
                                            setWelcomeModalVariant(
                                                'returning_no_saved'
                                            );
                                            setShowWelcomeModal(true);
                                        }
                                    }
                                } catch (error: any) {
                                    console.error(
                                        '[PAIR-APP] Error checking saved queue after campaign:',
                                        error
                                    );
                                    // Don't show modal if there's an auth error
                                    if (
                                        error?.status === 401 ||
                                        error?.message?.includes('401')
                                    ) {
                                        console.log(
                                            '[PAIR-APP] 401 error after campaign, clearing session'
                                        );
                                        clearSession();
                                    } else if (user && user.id) {
                                        // Only show modal for non-auth errors if we have a valid session
                                        // Check if user has a new coupon that needs to be shown
                                        if (
                                            couponData &&
                                            couponData.is_exists_for_user ===
                                                false
                                        ) {
                                            console.log(
                                                '[PAIR-APP] User has a new coupon (campaign error case), showing surprise gift modal instead of welcome back'
                                            );
                                            // Check if we have a gift channel from the campaign
                                            if (hasGiftChannels(couponData)) {
                                                setGiftRevealed(false); // Reset the flag when showing the modal
                                                setShowSurpriseGiftModal(true);
                                            } else {
                                                // Show AI personalization modal for new coupon without gift
                                                setShowAIPersonalizationModal(
                                                    true
                                                );
                                            }
                                        } else {
                                            setWelcomeModalVariant(
                                                'returning_no_saved'
                                            );
                                            setShowWelcomeModal(true);
                                        }
                                    }
                                } finally {
                                    setIsCheckingSession(false);
                                }
                            }
                        }}
                    />
                )}

                {/* Campaign Loading Overlay */}
                <CampaignLoadingOverlay
                    isVisible={isLoadingCoupon}
                    message="Loading campaign details..."
                />

                {/* Campaign Error Toast */}
                <CampaignErrorToast
                    isVisible={!!couponError}
                    message={couponError || ''}
                    onDismiss={() => setCouponError(null)}
                />

                {/* AI Curation Modal with new theme/tag interface */}
                <AnimatePresence>
                    {showAIPersonalizationModal && !isCheckingSession && (
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="fixed inset-0 bg-black/75 backdrop-blur-sm z-50 overflow-y-auto"
                            onClick={(e) => {
                                // Only close if clicking the backdrop and allowed to close
                                if (
                                    e.target === e.currentTarget &&
                                    currentStep === 'artcast' &&
                                    (!!aiSearchedChannels ||
                                        !!savedQueue ||
                                        !!giftChannels)
                                ) {
                                    setShowAIPersonalizationModal(false);
                                    console.log(
                                        '[PAIR-APP] AI modal closed by backdrop click'
                                    );
                                }
                            }}
                        >
                            <div className="min-h-full flex items-center justify-center p-4">
                                <motion.div
                                    initial={{ scale: 0.9, opacity: 0 }}
                                    animate={{ scale: 1, opacity: 1 }}
                                    exit={{ scale: 0.9, opacity: 0 }}
                                    transition={{
                                        type: 'spring',
                                        damping: 25,
                                        stiffness: 300,
                                    }}
                                    className="relative w-full max-w-md my-8"
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    {/* Close button - only show if allowed */}
                                    {currentStep === 'artcast' &&
                                        (!!aiSearchedChannels ||
                                            !!savedQueue ||
                                            !!giftChannels) && (
                                            <button
                                                onClick={() => {
                                                    setShowAIPersonalizationModal(
                                                        false
                                                    );
                                                    console.log(
                                                        '[PAIR-APP] AI modal closed without submitting'
                                                    );
                                                }}
                                                className="absolute top-4 right-4 text-white/70 hover:text-white transition-colors z-50"
                                            >
                                                <svg
                                                    className="w-6 h-6"
                                                    fill="none"
                                                    viewBox="0 0 24 24"
                                                    stroke="currentColor"
                                                >
                                                    <path
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                        strokeWidth={2}
                                                        d="M6 18L18 6M6 6l12 12"
                                                    />
                                                </svg>
                                            </button>
                                        )}

                                    <AICurationStep
                                        onSubmit={
                                            handleAIPersonalizationModalSubmit
                                        }
                                        isProcessing={isAIProcessing}
                                    />
                                </motion.div>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* Surprise Gift Modal */}
                <SurpriseGiftModal
                    isOpen={showSurpriseGiftModal}
                    onClose={() => {
                        setShowSurpriseGiftModal(false);
                        // When closing the surprise modal, check if gift was revealed
                        // If gift was revealed, skip AI personalization (already moved to artcast)
                        // If gift was not revealed (user just closed modal), show AI personalization
                        if (!giftRevealed && userId) {
                            console.log(
                                '[PAIR-APP] User closed surprise modal without revealing gift, opening AI personalization'
                            );
                            setShowAIPersonalizationModal(true);
                        } else if (!giftRevealed && !userId) {
                            console.log(
                                '[PAIR-APP] User closed surprise modal but not logged in, staying on current step'
                            );
                            // User needs to log in first
                            if (
                                currentStep !== 'email' &&
                                currentStep !== 'social'
                            ) {
                                setCurrentStep('email');
                            }
                        }
                    }}
                    onReveal={handleRevealGift}
                    couponData={couponData}
                />
            </div>
        </>
    );
}

export const getServerSideProps: GetServerSideProps = async (context) => {
    // Skip logging for Kubernetes health checks
    const userAgent = context.req.headers['user-agent'] || '';
    const isHealthCheck = userAgent.includes('kube-probe');

    if (!isHealthCheck) {
        console.log('[PAIR-APP] [SERVER] getServerSideProps: Function called');
        console.log(
            '[PAIR-APP] [SERVER] getServerSideProps: context.query:',
            JSON.stringify(context.query, null, 2)
        );
        console.log(
            '[PAIR-APP] [SERVER] getServerSideProps: context.req.url:',
            context.req.url
        );
        console.log(
            '[PAIR-APP] [SERVER] getServerSideProps: User-Agent:',
            userAgent
        );
        console.log(
            '[PAIR-APP] [SERVER] getServerSideProps: X-Forwarded-For:',
            context.req.headers['x-forwarded-for']
        );
        console.log(
            '[PAIR-APP] [SERVER] getServerSideProps: Environment check:'
        );
        console.log(
            '[PAIR-APP] [SERVER] getServerSideProps: NEXT_PUBLIC_API_BASE_URL:',
            process.env.NEXT_PUBLIC_API_BASE_URL
        );
        console.log(
            '[PAIR-APP] [SERVER] getServerSideProps: NODE_ENV:',
            process.env.NODE_ENV
        );
    }

    const { deviceId, deviceCode, shortSecret, coupon } = context.query;

    if (!isHealthCheck) {
        console.log(
            '[PAIR-APP] [SERVER] getServerSideProps: Extracted params:',
            { deviceId, deviceCode, shortSecret, coupon }
        );
    }

    // Check if we have deviceId with either deviceCode or shortSecret
    if (!deviceId || (!deviceCode && !shortSecret)) {
        if (!isHealthCheck) {
            console.log(
                '[PAIR-APP] [SERVER] getServerSideProps: Missing deviceId or both deviceCode and shortSecret, providing empty defaults'
            );
        }
        return {
            props: {
                deviceId: '',
                deviceCode: '',
            },
        };
    }

    if (!isHealthCheck) {
        console.log(
            '[PAIR-APP] [SERVER] getServerSideProps: Returning props successfully'
        );
    }
    return {
        props: {
            deviceId: deviceId as string,
            // Use deviceCode if available, otherwise use shortSecret
            deviceCode: (deviceCode || shortSecret) as string,
            shortSecret: (shortSecret as string) || '',
        },
    };
};
// Trigger rebuild Mon Sep  1 19:31:17 IDT 2025
// Force rebuild test Mon Sep  1 20:39:53 IDT 2025
